import { directive, Directive } from 'lit/directive.js';
import { LitElement, css, html } from 'lit';
import { state, query, property, customElement } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const ARIA_IDREF_ATTRIBUTES = new Map([
    ['aria-activedescendant', 'ariaActivedescendantElement'],
    ['aria-controls', 'ariaControlsElements'],
    ['aria-describedby', 'ariaDescribedByElements'],
    ['aria-details', 'ariaDetailsElements'],
    ['aria-errormessage', 'ariaErrormessageElements'],
    ['aria-flowto', 'ariaFlowtoElements'],
    ['aria-labelledby', 'ariaLabelledByElements'],
    ['aria-owns', 'ariaOwnsElements'],
]);
function resolveAriaIdRefs(element, value) {
    const ids = value
        .trim()
        .split(/\s+/)
        .filter((id) => id.length > 0);
    if (ids.length === 0)
        return null;
    const root = element.getRootNode();
    let resolvedElements = [];
    if (root instanceof ShadowRoot) {
        resolvedElements = ids.map((id) => root.getElementById(id));
    }
    else if (root instanceof Document) {
        resolvedElements = ids.map((id) => root.getElementById(id));
    }
    else {
        resolvedElements = ids.map((id) => document.getElementById(id));
    }
    if (resolvedElements.filter((el) => el !== null).length === 0) {
        resolvedElements = ids.map((id) => document.getElementById(id));
    }
    const validElements = resolvedElements.filter((el) => el !== null);
    return validElements.length === ids.length ? validElements : null;
}
function setInternalsProperty(el, name, resolvedElements) {
    if (name === 'aria-activedescendant') {
        const [firstElement] = resolvedElements;
        // eslint-disable-next-line no-param-reassign
        el[name] = firstElement;
    }
    else {
        // eslint-disable-next-line no-param-reassign
        el[name] = resolvedElements;
    }
}
class SetAttributesDirective extends Directive {
    update(part, [attributes]) {
        const el = part.element;
        const internals = el.internals ?? null;
        for (const [name, value] of Object.entries(attributes)) {
            if (value == null) {
                el.removeAttribute(name);
            }
            else {
                const internalsPropertyName = ARIA_IDREF_ATTRIBUTES.get(name);
                if (internalsPropertyName) {
                    const resolvedElements = resolveAriaIdRefs(el, String(value));
                    if (resolvedElements) {
                        if (internals) {
                            // Set on ElementInternals
                            setInternalsProperty(internals, internalsPropertyName, resolvedElements);
                        }
                        else {
                            // Set internals property on shadowdom item
                            el.setAttribute(name, String(value));
                            setInternalsProperty(el, internalsPropertyName, resolvedElements);
                        }
                    }
                }
                else {
                    // Non-ARIA IDREF attributes: set normally
                    el.setAttribute(name, String(value));
                }
            }
        }
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    render(_attributes) {
        return null;
    }
}
/* How to use:

<textarea ${setAttributes(this._ariaAttributes)} ... />
*/
directive(SetAttributesDirective);

function r(e){var t,f,n="";if("string"==typeof e||"number"==typeof e)n+=e;else if("object"==typeof e)if(Array.isArray(e)){var o=e.length;for(t=0;t<o;t++)e[t]&&(f=r(e[t]))&&(n&&(n+=" "),n+=f);}else for(f in e)e[f]&&(n&&(n+=" "),n+=f);return n}function clsx(){for(var e,t,f=0,n="",o=arguments.length;f<o;f++)(e=arguments[f])&&(t=r(e))&&(n&&(n+=" "),n+=t);return n}

const connectedElements = new Set();
if (window && !window.translations) {
    window.translations = new Map();
}
const { translations } = window;
let fallback;
// TODO: We need some way for users to be able to set these on the server.
let documentDirection = 'ltr';
// Fallback for server.
let documentLanguage = 'en';
const isClient = typeof MutationObserver !== 'undefined' &&
    typeof document !== 'undefined' &&
    typeof document.documentElement !== 'undefined';
/** Updates all localized elements that are currently connected */
function update() {
    if (isClient) {
        documentDirection = document.documentElement.dir || 'ltr';
        documentLanguage = document.documentElement.lang || navigator.language;
    }
    [...connectedElements.keys()].forEach((el) => {
        const litEl = el;
        if (typeof litEl.requestUpdate === 'function') {
            litEl.requestUpdate();
        }
    });
}
if (isClient) {
    const documentElementObserver = new MutationObserver(update);
    documentDirection = document.documentElement.dir || 'ltr';
    documentLanguage = document.documentElement.lang || navigator.language;
    // Watch for changes on <html lang>
    documentElementObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['dir', 'lang'],
    });
}
/** Registers one or more translations */
function registerTranslation(...translation) {
    translation.forEach((t) => {
        const code = t.$code.toLowerCase();
        if (translations.has(code)) {
            // Merge translations that share the same language code
            translations.set(code, { ...translations.get(code), ...t });
        }
        else {
            translations.set(code, t);
        }
        // The first translation that's registered is the fallback
        if (!fallback) {
            fallback = t;
        }
    });
    update();
}
if (typeof window !== 'undefined') {
    window.registerTranslation = registerTranslation;
}
/**
 * Localize Reactive Controller for components built with Lit
 *
 * To use this controller, import the class and instantiate it in a custom element constructor:
 *
 * private localize = new LocalizeController(this);
 *
 * This will add the element to the set and make it respond to changes to <html dir|lang> automatically. To make it
 * respond to changes to its own dir|lang properties, make it a property:
 *
 *   @property() dir: string;
 *   @property() lang: string;
 *
 * To use a translation method, call it like this:
 *
 *   ${this.localize.term('term_key_here')}
 *   ${this.localize.date('2021-12-03')}
 *   ${this.localize.number(1000000)}
 */
class LocalizeController {
    constructor(host) {
        this.host = host;
        this.host.addController(this);
    }
    hostConnected() {
        connectedElements.add(this.host);
    }
    hostDisconnected() {
        connectedElements.delete(this.host);
    }
    /**
     * Gets the host element's directionality as determined by the `dir` attribute. The return value is transformed to
     * lowercase.
     */
    dir() {
        return `${this.host.dir || documentDirection}`.toLowerCase();
    }
    /**
     * Gets the host element's language as determined by the `lang` attribute. The return value is transformed to
     * lowercase.
     */
    lang() {
        return `${this.host.lang || documentLanguage}`.toLowerCase();
    }
    // eslint-disable-next-line class-methods-use-this
    getTranslationData(lang) {
        // Convert "en_US" to "en-US". Note that both underscores and dashes are allowed per spec, but underscores result in
        // a RangeError by the call to `new Intl.Locale()`. See: https://unicode.org/reports/tr35/#unicode-locale-identifier
        const locale = new Intl.Locale(lang.replace(/_/g, '-'));
        const language = locale?.language.toLowerCase();
        const region = locale?.region?.toLowerCase() ?? '';
        const primary = translations.get(`${language}-${region}`);
        const secondary = translations.get(language);
        return { locale, language, region, primary, secondary };
    }
    /** Determines if the specified term exists, optionally checking the fallback translation. */
    exists(key, options) {
        const { primary, secondary } = this.getTranslationData(options.lang ?? this.lang());
        const mergedOptions = {
            includeFallback: false,
            ...options,
        };
        if ((primary && primary[key]) ||
            (secondary && secondary[key]) ||
            (mergedOptions.includeFallback && fallback && fallback[key])) {
            return true;
        }
        return false;
    }
    /** Outputs a translated term. */
    term(key, ...args) {
        const { primary, secondary } = this.getTranslationData(this.lang());
        let term;
        // Look for a matching term using regionCode, code, then the fallback
        if (primary && primary[key]) {
            term = primary[key];
        }
        else if (secondary && secondary[key]) {
            term = secondary[key];
        }
        else if (fallback && fallback[key]) {
            term = fallback[key];
        }
        else {
            // eslint-disable-next-line no-console
            console.error(`No translation found for: ${String(key)}`);
            return String(key);
        }
        if (typeof term === 'function') {
            return term(...args);
        }
        return term;
    }
    /** Outputs a localized date in the specified format. */
    date(dateToFormat, options) {
        const date = new Date(dateToFormat);
        return new Intl.DateTimeFormat(this.lang(), options).format(date);
    }
    /** Outputs a localized number in the specified format. */
    number(numberToFormat, options) {
        const num = Number(numberToFormat);
        return Number.isNaN(num) ? '' : new Intl.NumberFormat(this.lang(), options).format(num);
    }
    /** Outputs a localized time in relative format. */
    relativeTime(value, unit, options) {
        return new Intl.RelativeTimeFormat(this.lang(), options).format(value, unit);
    }
}

/**
 * @param Base The base class.
 * @returns A mix-in implementing `localizations` method.
 *
 *@example
 *    <!-- Terms -->
      ${this.$localize.term('hello')}
      or
      ${this.$t('hello')}

      <!-- Dates -->
      ${this.$localize.date('2021-09-15 14:00:00 ET', { month: 'long', day: 'numeric', year: 'numeric' })}
      or
      ${this.$d('2021-09-15 14:00:00 ET', { month: 'long', day: 'numeric', year: 'numeric' })}

      <!-- Numbers/currency -->
      ${this.$localize.number(1000, { style: 'currency', currency: 'USD'})}
      or
      ${this.$n(1000,{ style: 'currency', currency: 'USD'})}

      <!-- Determining language -->
      ${this.$localize.lang()}

      <!-- Determining directionality, e.g. 'ltr' or 'rtl' -->
      ${this.$localize.dir()}


    *** HOW TO DEFINE TRANSLATIONS: ***
    // Simple terms
    upload: 'Upload',

    // Terms with placeholders
    greetUser: (name: string) => `Hello, ${name}!`,

    // Plurals
    numFilesSelected: (count: number) => {
        if (count === 0) return 'No files selected';
        if (count === 1) return '1 file selected';
        return `${count} files selected`;
    }
 */
const LocalizeMixin = (Base) => class extends Base {
    constructor() {
        super(...arguments);
        this.localize = new LocalizeController(this);
    }
    // Provide default values to avoid definite assignment errors and avoid decorators
    // commentati perchè danno problemi su React.js. Sono attributi nativi, e assegnare un valore di default a react da fastidio
    // dir: string = '';
    // lang: string = '';
    /**
     * Restituisce tutta l'utility di traduzione
     *

     *
     * @returns tutta l'utility di traduzione
     *
     * @example
     * this.$localize.lang() -> ritorna la lingua corrente
     * this.$localize.dir() -> ritorna la direzione della lingua corrente
     */
    get $localize() {
        return this.localize;
    }
    /**
     * Restituisce una stringa localizzata a partire da una chiave di termine.
     *
     * Utilizza il `LocalizeController` per accedere al dizionario corrente e
     * tradurre la chiave fornita secondo la lingua attiva.
     *
     * @param t - La chiave del termine da localizzare (es. 'hello', 'submit', ecc.).
     * @returns La stringa tradotta in base alla lingua attiva. Se la chiave non è trovata, restituisce la chiave stessa.
     *
     * @example
     * this.$t('hello'); // → "Ciao" (in locale it-IT)
     */
    $t(t) {
        // format term
        return this.localize.term(t);
    }
    /**
     * Formatta una data in base alla localizzazione attiva.
     *
     * Utilizza il `LocalizeController` per restituire una stringa localizzata
     * secondo le opzioni fornite (es. mese esteso, anno, ecc.).
     *
     * @param n - La data da formattare come stringa compatibile (es. ISO o con timezone, es. '2021-09-15 14:00:00 ET').
     * @param p - Le opzioni di formattazione per `Intl.DateTimeFormat` (es. { year: 'numeric', month: 'long', day: 'numeric' }).
     * @returns Una stringa rappresentante la data formattata secondo la localizzazione attiva.
     *
     * @example
     * this.$d('2021-09-15 14:00:00 ET', { year: 'numeric', month: 'long', day: 'numeric' });
     * // → "15 settembre 2021" (in locale it-IT)
     */
    $d(d, p) {
        // format date
        return this.localize.date(d, p);
    }
    /**
     * Formatta un numero secondo le impostazioni locali dell'utente corrente.
     *
     * Utilizza il `LocalizeController` per applicare formattazione numerica,
     * incluse opzioni come separatori, decimali, valute, ecc.
     *
     * @param d - Il numero da formattare.
     * @param p - Le opzioni di formattazione (es. { style: 'currency', currency: 'EUR' }).
     * @returns Una stringa rappresentante il numero formattato secondo la localizzazione attiva.
     *
     * @example
     * this.$n(1234.56, { style: 'currency', currency: 'USD' }); // → "$1,234.56" (in locale en-US)
     */
    $n(d, p) {
        return this.localize.number(d, p);
    }
};

/* eslint-disable no-console */
class Logger {
    constructor(tag) {
        this.tag = tag;
    }
    format(level, msg) {
        return [`[${this.tag}] ${msg}`];
    }
    warn(msg) {
        console.warn(...this.format('warn', msg));
    }
    error(msg) {
        console.error(...this.format('error', msg));
    }
    info(msg) {
        console.info(...this.format('info', msg));
    }
}

/**
 * Factory function per creare una base class estendibile
 * con stili personalizzati.
 */
class BaseComponent extends LitElement {
    constructor() {
        super();
        this.composeClass = clsx;
        this.logger = new Logger(this.tagName.toLowerCase());
    }
    get _ariaAttributes() {
        const attributes = {};
        for (const attr of this.getAttributeNames()) {
            if (attr === 'it-role') {
                attributes.role = this.getAttribute(attr);
            }
            else if (attr.startsWith('it-aria-')) {
                attributes[attr.replace(/^it-/, '')] = this.getAttribute(attr);
            }
        }
        return attributes;
    }
    // eslint-disable-next-line class-methods-use-this
    generateId(prefix) {
        return `${prefix}-${Math.random().toString(36).slice(2)}`;
    }
    // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
    addFocus(element) {
        // new TrackFocus(element); // per il momento è stato disattivato perchè ci sono le pseudo classi ::focus-visible per fare quello che fa TrackFocus. Si possono aggiungere regole css in bsi-italia 3 dato che stiamo facendo una breaking release di bsi.
    }
    // eslint-disable-next-line class-methods-use-this
    getActiveElement() {
        let active = document.activeElement;
        while (active && active.shadowRoot && active.shadowRoot.activeElement) {
            active = active.shadowRoot.activeElement;
        }
        return active;
    }
    get focusElement() {
        return this;
    }
    // eslint-disable-next-line class-methods-use-this
    get prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    connectedCallback() {
        super.connectedCallback();
        // generate internal _id
        const prefix = this.id?.length > 0 ? this.id : this.tagName.toLowerCase();
        this._id = this.generateId(prefix);
        this.ariaAttributesObserver = new MutationObserver((mutations) => {
            const shouldUpdate = mutations.some((mutation) => {
                if (mutation.type !== 'attributes' || !mutation.attributeName) {
                    return false;
                }
                return mutation.attributeName === 'it-role' || mutation.attributeName.startsWith('it-aria-');
            });
            if (shouldUpdate) {
                this.requestUpdate();
            }
        });
        this.ariaAttributesObserver.observe(this, {
            attributes: true,
            attributeOldValue: true,
        });
    }
    disconnectedCallback() {
        this.ariaAttributesObserver?.disconnect();
        this.ariaAttributesObserver = undefined;
        super.disconnectedCallback();
    }
}
const BaseLocalizedComponent = LocalizeMixin(BaseComponent);

/**
 * Safari/VoiceOver iOS workarounds catalogue — why timers exist here:
 *
 * 1. `activate()` rAF + setTimeout(50): Safari does not reliably update the VoiceOver
 *    accessibility tree synchronously. rAF ensures the element is painted before we
 *    attempt to focus it. setTimeout(50) yields 50ms so VoiceOver can settle its
 *    accessibility tree before announcing the newly focused element.
 *    Empirically, VoiceOver iOS needs 100–300ms; the animated path in it-modal already
 *    provides ~316ms naturally via animation.finished, so the 50ms here only truly
 *    matters for the no-animation / prefers-reduced-motion path.
 *
 * 2. `_restoreFocus()` setTimeout(0): Same settle reason on close. The caller
 *    (it-modal finishHide) adds a belt-and-suspenders 20ms setTimeout; _restoreFocus
 *    is skipped via { skipFocusRestore: true } in that case to avoid double-focus.
 *
 * 3. `activate()` iOS body-focus fallback: On iOS Safari, tapping a button does not
 *    reliably move document.activeElement away from body. We fall back to getTrigger()
 *    so _previousActiveElement is always a real DOM element to restore focus to on close.
 *
 * There is no WebKit/Safari API to detect when VoiceOver has settled its accessibility
 * tree. setTimeout-based workarounds are the same pattern used by Bootstrap 5, the
 * focus-trap npm package, and a11y-dialog.
 */
class FocusTrapController {
    static getManagedHostAncestor(element) {
        let current = element;
        while (current) {
            const parentEl = current instanceof HTMLElement ? current.parentElement : null;
            if (parentEl && parentEl.matches(FocusTrapController.MANAGED_HOST_SELECTORS))
                return parentEl;
            if (parentEl) {
                current = parentEl;
                continue;
            }
            const rootNode = current.getRootNode();
            if (rootNode instanceof ShadowRoot) {
                const hostElement = rootNode.host;
                if (hostElement instanceof HTMLElement) {
                    if (hostElement.matches(FocusTrapController.MANAGED_HOST_SELECTORS))
                        return hostElement;
                    current = hostElement;
                    continue;
                }
            }
            break;
        }
        return null;
    }
    static isInsideManagedHost(element) {
        return FocusTrapController.getManagedHostAncestor(element) !== null;
    }
    static getManagedMenuHostAncestor(element) {
        let current = element;
        while (current) {
            const parentEl = current instanceof HTMLElement ? current.parentElement : null;
            if (parentEl && parentEl.matches(FocusTrapController.MANAGED_MENU_HOST_SELECTORS))
                return parentEl;
            if (parentEl) {
                current = parentEl;
                continue;
            }
            const rootNode = current.getRootNode();
            if (rootNode instanceof ShadowRoot) {
                const hostElement = rootNode.host;
                if (hostElement instanceof HTMLElement) {
                    if (hostElement.matches(FocusTrapController.MANAGED_MENU_HOST_SELECTORS))
                        return hostElement;
                    current = hostElement;
                    continue;
                }
            }
            break;
        }
        return null;
    }
    static collectFocusableCandidates(root, candidates, trigger, visited) {
        const rootAsNode = root;
        if (visited.has(rootAsNode))
            return;
        visited.add(rootAsNode);
        const elements = Array.from(root.querySelectorAll('*'));
        elements.forEach((el) => {
            if (el === trigger)
                return;
            // Keep custom host components as single tab stops: their internal light/shadow
            // descendants must not appear as separate trap candidates.
            if (FocusTrapController.isInsideManagedHost(el))
                return;
            if (el.matches(FocusTrapController.FOCUSABLE_SELECTORS)) {
                candidates.push(el);
                // Stop here: focusCandidate() handles delegation to the inner focusable.
                // Prevent recursion into this element's shadow DOM so inner buttons/triggers
                // are not added as separate tab stops.
                if (el.shadowRoot)
                    visited.add(el.shadowRoot);
                return;
            }
            if (el instanceof HTMLSlotElement) {
                el.assignedElements({ flatten: true }).forEach((assigned) => {
                    if (!(assigned instanceof HTMLElement) || assigned === trigger)
                        return;
                    if (assigned.matches(FocusTrapController.FOCUSABLE_SELECTORS)) {
                        candidates.push(assigned);
                        // Same rule: don't recurse into a focusable element.
                        if (assigned.shadowRoot)
                            visited.add(assigned.shadowRoot);
                        return;
                    }
                    FocusTrapController.collectFocusableCandidates(assigned, candidates, trigger, visited);
                    if (assigned.shadowRoot) {
                        FocusTrapController.collectFocusableCandidates(assigned.shadowRoot, candidates, trigger, visited);
                    }
                });
                return;
            }
            if (el.shadowRoot) {
                FocusTrapController.collectFocusableCandidates(el.shadowRoot, candidates, trigger, visited);
            }
        });
    }
    static getFirstFocusableInShadow(host) {
        const visited = new Set();
        const walk = (root) => {
            const rootAsNode = root;
            if (visited.has(rootAsNode))
                return null;
            visited.add(rootAsNode);
            const direct = Array.from(root.querySelectorAll(FocusTrapController.NATIVE_FOCUSABLE_SELECTORS));
            const firstDirect = direct.find((el) => FocusTrapController.isFocusable(el));
            if (firstDirect)
                return firstDirect;
            const all = Array.from(root.querySelectorAll('*'));
            for (const el of all) {
                if (el instanceof HTMLSlotElement) {
                    const assigned = el.assignedElements({ flatten: true });
                    for (const assignedEl of assigned) {
                        if (!(assignedEl instanceof HTMLElement))
                            continue;
                        if (assignedEl.matches(FocusTrapController.NATIVE_FOCUSABLE_SELECTORS)) {
                            if (FocusTrapController.isFocusable(assignedEl))
                                return assignedEl;
                        }
                        const nestedAssigned = walk(assignedEl);
                        if (nestedAssigned)
                            return nestedAssigned;
                        if (assignedEl.shadowRoot) {
                            const nestedAssignedShadow = walk(assignedEl.shadowRoot);
                            if (nestedAssignedShadow)
                                return nestedAssignedShadow;
                        }
                    }
                }
                if (el.shadowRoot) {
                    const nested = walk(el.shadowRoot);
                    if (nested)
                        return nested;
                }
            }
            return null;
        };
        if (!host.shadowRoot)
            return null;
        return walk(host.shadowRoot);
    }
    static focusCandidate(target) {
        if (!target)
            return;
        if (target.tagName.toLowerCase().startsWith('it-')) {
            const innerFocusable = FocusTrapController.getFirstFocusableInShadow(target);
            if (innerFocusable) {
                innerFocusable.focus({ preventScroll: true });
                return;
            }
        }
        target.focus({ preventScroll: true });
    }
    /**
     * Verifica se un elemento è effettivamente focusabile e tabbabile.
     * Elementi con tabindex="-1" sono focusabili programmaticamente ma non tabbabili.
     */
    static isFocusable(el) {
        if (typeof el.focus !== 'function')
            return false;
        if (el.hasAttribute('disabled') || el.hasAttribute('inert') || el.hasAttribute('it-inert'))
            return false;
        const tabindex = el.getAttribute('tabindex');
        if (tabindex && parseInt(tabindex, 10) < 0)
            return false;
        if (el.tagName.toLowerCase().startsWith('it-')) {
            if (tabindex && parseInt(tabindex, 10) < 0)
                return false;
            return true;
        }
        // Fix Race Condition Safari: durante l'apertura non fidarti di getClientRects o opacità
        try {
            const style = window.getComputedStyle(el);
            return style && style.display !== 'none' && style.visibility !== 'hidden';
        }
        catch {
            return false;
        }
    }
    constructor(host, config) {
        this.observer = null;
        this._isActive = false;
        this._previousActiveElement = null;
        this._focusableElements = [];
        /** Last known index resolved from real focusin events inside the trap.
         *  Used as a fallback when assistive tech retargeting makes activeElement
         *  temporarily unmappable during Tab navigation. */
        this._lastKnownFocusIndex = -1;
        /** Timer ID for the focus call scheduled inside activate(). Cancelled by deactivate()
         *  to prevent focus firing after the trap has been torn down (fast open/close, unmount). */
        this._pendingFocusTimer = null;
        this._handleKeyDown = (event) => {
            if (!this._isActive)
                return;
            switch (event.key) {
                case 'Escape':
                    if (!this.config.disableEscape && this.config.onEscape) {
                        event.preventDefault();
                        this.config.onEscape();
                    }
                    break;
                case 'Tab':
                    this._handleTab(event);
                    break;
            }
        };
        this._handleFocusIn = (event) => {
            if (!this._isActive)
                return;
            if (this._focusableElements.length === 0) {
                this.updateFocusableElements();
                if (this._focusableElements.length === 0)
                    return;
            }
            const path = event.composedPath();
            let index = this._focusableElements.findIndex((candidate) => path.includes(candidate));
            if (index === -1) {
                const target = path[0];
                if (target instanceof HTMLElement) {
                    index = this._focusableElements.indexOf(target);
                }
            }
            if (index === -1) {
                const active = FocusTrapController.getActiveElement();
                if (active) {
                    index = this._focusableElements.findIndex((candidate) => candidate.contains(active));
                }
            }
            if (index !== -1) {
                this._lastKnownFocusIndex = index;
            }
        };
        this.host = host;
        this.config = config;
        this.host.addController(this);
    }
    hostConnected() {
        const observerOptions = { childList: true, attributes: true, subtree: true };
        this.observer = new MutationObserver(this.updateFocusableElements.bind(this));
        this.observer.observe(this.host, observerOptions);
    }
    hostDisconnected() {
        this.deactivate();
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
    }
    get isActive() {
        return this._isActive;
    }
    activate() {
        if (this._isActive)
            return;
        this._isActive = true;
        // FIX iOS: Safari non dà focus al tap. Se activeElement è body, salviamo il trigger della config.
        const currentActive = FocusTrapController.getActiveElement();
        this._previousActiveElement =
            !currentActive || currentActive === document.body ? this.config.getTrigger() : currentActive;
        // Usa document capture: cattura Tab/Escape prima di qualsiasi handler,
        // indipendentemente da shadow DOM, delegatesFocus e slot retargeting.
        document.addEventListener('keydown', this._handleKeyDown, true);
        document.addEventListener('focusin', this._handleFocusIn, true);
        this.updateFocusableElements();
        this._lastKnownFocusIndex = -1;
        // FIX Safari: trick engine
        // Diamo al browser un istante per processare il ricalcolo del layout
        requestAnimationFrame(() => {
            if (this.config.initialFocus) {
                const el = this.config.initialFocus();
                if (el) {
                    // 50ms gives VoiceOver iOS time to settle its accessibility tree before
                    // announcing the focused element. See class-level comment for details.
                    this._pendingFocusTimer = setTimeout(() => {
                        this._pendingFocusTimer = null;
                        el.focus({ preventScroll: true });
                    }, 50);
                }
            }
            else {
                // eslint-disable-next-line no-console
                console.warn("FocusTrap: missing 'initialFocus' callback, provide one");
            }
        });
    }
    deactivate(options) {
        if (!this._isActive)
            return;
        // Cancel any in-flight focus timer so it cannot fire after the trap is torn down
        // (e.g. fast open/close cycle, component unmounted while opening).
        if (this._pendingFocusTimer !== null) {
            clearTimeout(this._pendingFocusTimer);
            this._pendingFocusTimer = null;
        }
        this._isActive = false;
        document.removeEventListener('keydown', this._handleKeyDown, true);
        document.removeEventListener('focusin', this._handleFocusIn, true);
        this._lastKnownFocusIndex = -1;
        if (!options?.skipFocusRestore) {
            this._restoreFocus();
        }
        else {
            this._previousActiveElement = null;
        }
    }
    /**
     * Aggiorna la lista degli elementi focusabili.
     * Chiamare dopo modifiche al DOM.
     */
    updateFocusableElements() {
        const container = this.config.getContainer();
        const trigger = this.config.getTrigger();
        if (!container) {
            this._focusableElements = [];
            this._first = undefined;
            this._last = undefined;
            return;
        }
        const candidates = [];
        FocusTrapController.collectFocusableCandidates(container, candidates, trigger, new Set());
        // Include anche i figli light DOM dell'host (es. contenuti slottati della modale)
        Array.from(this.host.children).forEach((child) => {
            if (!(child instanceof HTMLElement) || child === trigger)
                return;
            if (child.matches(FocusTrapController.FOCUSABLE_SELECTORS)) {
                candidates.push(child);
                // Don't recurse: same stop-on-focusable rule.
                return;
            }
            const childVisited = new Set();
            FocusTrapController.collectFocusableCandidates(child, candidates, trigger, childVisited);
            if (child.shadowRoot) {
                FocusTrapController.collectFocusableCandidates(child.shadowRoot, candidates, trigger, childVisited);
            }
        });
        // Filtra solo elementi veramente focusabili e rimuovi duplicati
        this._focusableElements = Array.from(new Set(candidates)).filter((el) => {
            // Escludi esplicitamente il container/dialog stesso
            if (el === this.config.getContainer())
                return false;
            return FocusTrapController.isFocusable(el);
        });
        // Aggiorna primo/ultimo
        this._first = this._focusableElements?.[0];
        this._last = this._focusableElements[this._focusableElements.length - 1];
    }
    /** Sposta il focus sul primo elemento focusabile */
    focusFirst() {
        if (this._first)
            this._first.focus();
    }
    /** Sposta il focus sull'ultimo elemento focusabile */
    focusLast() {
        if (this._last) {
            this._last.focus();
        }
    }
    /** Ritorna l'elemento attivo corrente, attraversando shadow DOM e slot */
    static getActiveElement() {
        let active = document.activeElement;
        while (active && active.shadowRoot && active.shadowRoot.activeElement) {
            active = active.shadowRoot.activeElement;
        }
        return active;
    }
    _handleTab(event) {
        const activeElement = FocusTrapController.getActiveElement();
        // Let native Tab behavior handle navigation inside open dropdown/megamenu.
        // This allows entering and traversing internal menu items correctly.
        if (activeElement) {
            const managedHost = FocusTrapController.getManagedMenuHostAncestor(activeElement);
            if (managedHost && managedHost.getAttribute('data-it-aria-expanded') === 'true') {
                return;
            }
        }
        // Ricalcola sempre al Tab: gli elementi slottati possono completare il render
        // del proprio shadow DOM dopo l'attivazione iniziale del trap.
        this.updateFocusableElements();
        if (this._focusableElements.length === 0)
            return; // Se è ancora vuoto, non bloccare il Tab
        event.preventDefault();
        let currentIndex = this._focusableElements.indexOf(activeElement);
        if (currentIndex === -1 && activeElement) {
            // 1. Walk shadow host chain (covers delegatesFocus custom elements)
            let parent = activeElement.getRootNode()?.host;
            while (parent && currentIndex === -1) {
                currentIndex = this._focusableElements.indexOf(parent);
                if (currentIndex === -1)
                    parent = parent.getRootNode()?.host;
            }
        }
        if (currentIndex === -1) {
            // 2. Composed path fallback: handles focus inside nested shadow/slotted
            //    content (e.g. anchors inside it-dropdown-item) by mapping the active
            //    target to the first focusable host found in the event path.
            const composedPath = event.composedPath();
            currentIndex = this._focusableElements.findIndex((candidate) => composedPath.includes(candidate));
        }
        if (currentIndex === -1 && activeElement) {
            // 3. contains() fallback: handles focus inside an open dropdown/megamenu whose
            //    items live in light DOM (not shadow DOM), so the parent-walk above doesn't
            //    reach the host.  Map to the host's index so Tab/Shift+Tab move to the
            //    correct adjacent element.
            currentIndex = this._focusableElements.findIndex((candidate) => candidate.contains(activeElement));
        }
        if (currentIndex === -1) {
            // 4. AT fallback: with some screen readers, document.activeElement and/or
            //    event target can be unreliable while navigating inside composite widgets.
            //    If a dropdown/megamenu is currently open, use its host index as anchor
            //    so forward/backward Tab continue from that position instead of wrapping.
            currentIndex = this._focusableElements.findIndex((candidate) => candidate.matches('it-dropdown, it-megamenu') && candidate.getAttribute('data-it-aria-expanded') === 'true');
        }
        if (currentIndex === -1 && this._lastKnownFocusIndex >= 0) {
            // 5. Last-known fallback: if assistive tech retargeting hides the active
            //    element for this key event, continue from the most recent resolved
            //    focus index captured via focusin.
            currentIndex = Math.min(this._lastKnownFocusIndex, this._focusableElements.length - 1);
        }
        if (event.shiftKey) {
            if (currentIndex <= 0)
                FocusTrapController.focusCandidate(this._last);
            else
                FocusTrapController.focusCandidate(this._focusableElements[currentIndex - 1]);
        }
        else if (currentIndex === -1 || currentIndex >= this._focusableElements.length - 1)
            FocusTrapController.focusCandidate(this._first);
        else
            FocusTrapController.focusCandidate(this._focusableElements[currentIndex + 1]);
    }
    _restoreFocus() {
        const target = this._previousActiveElement;
        this._previousActiveElement = null;
        if (target && typeof target.focus === 'function' && document.contains(target)) {
            // FIX Safari: blur the current element first so VoiceOver doesn't stay on the
            // modal element while the accessibility tree processes the focus change.
            FocusTrapController.getActiveElement()?.blur();
            // FIX Safari/VoiceOver iOS: defer to the next macrotask so the accessibility
            // tree has time to process the DOM mutation before announcing the restored element.
            setTimeout(() => {
                target.focus({ preventScroll: true });
            }, 0);
        }
    }
}
FocusTrapController.FOCUSABLE_SELECTORS = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    '[tabindex]',
    'it-button:not([disabled])',
    'it-dropdown:not([disabled])',
    'it-megamenu:not([disabled])',
].join(',');
FocusTrapController.NATIVE_FOCUSABLE_SELECTORS = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');
FocusTrapController.MANAGED_HOST_SELECTORS = [
    'it-button:not([disabled])',
    'it-dropdown:not([disabled])',
    'it-megamenu:not([disabled])',
].join(',');
FocusTrapController.MANAGED_MENU_HOST_SELECTORS = [
    'it-dropdown:not([disabled])',
    'it-megamenu:not([disabled])',
].join(',');

//
// We store a WeakMap of forms + controls so we can keep references to all FormControl within a given form. As
// elements connect and disconnect to/from the DOM, their containing form is used as the key and the form control is
// added and removed from the form's set, respectively.
//
const formCollections = new WeakMap();
//
// We store a WeakMap of reportValidity() overloads so we can override it when form controls connect to the DOM and
// restore the original behavior when they disconnect.
//
const reportValidityOverloads = new WeakMap();
const checkValidityOverloads = new WeakMap();
//
// We store a Set of controls that users have interacted with. This allows us to determine the interaction state
// without littering the DOM with additional data attributes.
//
const userInteractedControls = new WeakSet();
//
// We store a WeakMap of interactions for each form control so we can track when all conditions are met for validation.
//
const interactions = new WeakMap();
/** A reactive controller to allow form controls to participate in form submission, validation, etc. */
class FormControlController {
    constructor(host, options) {
        this.submittedOnce = false;
        this.handleFormData = (event) => {
            // console.log('handleFormData');
            const disabled = this.options.disabled(this.host);
            const name = this.options.name(this.host);
            const value = this.options.value(this.host);
            const tagName = this.host.tagName.toLowerCase();
            // For buttons, we only submit the value if they were the submitter. This is currently done in doAction() by
            // injecting the name/value on a temporary button, so we can just skip them here.
            const isButton = tagName === 'it-button';
            if (this.host.isConnected &&
                !disabled &&
                !isButton &&
                typeof name === 'string' &&
                name.length > 0 &&
                typeof value !== 'undefined') {
                switch (tagName) {
                    case 'it-radio':
                        if (this.host.checked) {
                            event.formData.append(name, value);
                        }
                        break;
                    case 'it-checkbox':
                    case 'it-toggle':
                        if (this.host.checked) {
                            if (event.formData.getAll(name).indexOf(value) < 0) {
                                // handle group checkbox
                                event.formData.append(name, value);
                            }
                        }
                        break;
                    case 'it-checkbox-group':
                    case 'it-toggle-group':
                        // non settare valori in formData, perchè ogni singola checkbox setta il suo valore
                        break;
                    case 'it-upload':
                        // value is File[] — append each File object directly (not as string)
                        if (Array.isArray(value)) {
                            value.forEach((file) => {
                                event.formData.append(name, file);
                            });
                        }
                        break;
                    default:
                        if (Array.isArray(value)) {
                            value.forEach((val) => {
                                event.formData.append(name, val.toString());
                            });
                        }
                        else {
                            event.formData.append(name, value.toString());
                        }
                }
            }
        };
        this.handleFormSubmit = (event) => {
            const disabled = this.options.disabled(this.host);
            const reportValidity = this.options.reportValidity;
            // Update the interacted state for all controls when the form is submitted
            if (this.form && !this.form.noValidate) {
                formCollections.get(this.form)?.forEach((control) => {
                    this.setUserInteracted(control, true);
                });
            }
            const resReportValidity = reportValidity(this.host);
            if (this.form && !this.form.noValidate && !disabled && !resReportValidity) {
                event.preventDefault();
                // event.stopImmediatePropagation(); // se lo attiviamo, valida un campo alla volta
                // Scroll al primo controllo non valido
                const formControls = formCollections.get(this.form);
                if (formControls) {
                    for (const control of formControls) {
                        if (!control.validity?.valid) {
                            // Scroll smooth verso il controllo non valido
                            control.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center',
                            });
                            break;
                        }
                    }
                }
            }
            this.submittedOnce = true;
        };
        this.handleFormReset = () => {
            this.options.setValue(this.host, '');
            this.submittedOnce = false;
            this.setUserInteracted(this.host, false);
            interactions.set(this.host, []);
        };
        this.handleInteraction = (event) => {
            const emittedEvents = interactions.get(this.host);
            if (!emittedEvents.includes(event.type)) {
                emittedEvents.push(event.type);
            }
            // Mark it as user-interacted as soon as all associated events have been emitted
            if (emittedEvents.length === this.options.assumeInteractionOn.length) {
                this.setUserInteracted(this.host, true);
            }
        };
        this.checkFormValidity = () => {
            // console.log('checkFormValidity');
            //
            // This is very similar to the `reportFormValidity` function, but it does not trigger native constraint validation
            // Allow the user to simply check if the form is valid and handling validity in their own way.
            //
            // We preserve the original method in a WeakMap, but we don't call it from the overload because that would trigger
            // validations in an unexpected order. When the element disconnects, we revert to the original behavior. This won't
            // be necessary once we can use ElementInternals.
            //
            // Note that we're also honoring the form's novalidate attribute.
            //
            if (this.form && !this.form.noValidate) {
                // This seems sloppy, but checking all elements will cover native inputs, Shoelace inputs, and other custom
                // elements that support the constraint validation API.
                const elements = this.form.querySelectorAll('*');
                for (const element of elements) {
                    if (typeof element.checkValidity === 'function') {
                        if (!element.checkValidity()) {
                            return false;
                        }
                    }
                }
            }
            return true;
        };
        this.reportFormValidity = () => {
            // console.log('reportFormValidity');
            //
            // FormControl work hard to act like regular form controls. They support the Constraint Validation API
            // and its associated methods such as setCustomValidity() and reportValidity(). However, the HTMLFormElement also
            // has a reportValidity() method that will trigger validation on all child controls. Since we're not yet using
            // ElementInternals, we need to overload this method so it looks for any element with the reportValidity() method.
            //
            // We preserve the original method in a WeakMap, but we don't call it from the overload because that would trigger
            // validations in an unexpected order. When the element disconnects, we revert to the original behavior. This won't
            // be necessary once we can use ElementInternals.
            //
            // Note that we're also honoring the form's novalidate attribute.
            //
            if (this.form && !this.form.noValidate) {
                // This seems sloppy, but checking all elements will cover native inputs, Shoelace inputs, and other custom
                // elements that support the constraint validation API.
                const elements = this.form.querySelectorAll('*');
                for (const element of elements) {
                    if (typeof element.reportValidity === 'function') {
                        if (!element.reportValidity()) {
                            return false;
                        }
                    }
                }
            }
            return true;
        };
        (this.host = host).addController(this);
        this.options = {
            form: (input) => {
                // If there's a form attribute, use it to find the target form by id
                // Controls may not always reflect the 'form' property. For example, `<it-button>` doesn't reflect.
                const formId = input.form;
                if (formId) {
                    const root = input.getRootNode();
                    const form = root.querySelector(`#${formId}`);
                    if (form) {
                        return form;
                    }
                }
                return input.closest('form');
            },
            name: (input) => input.name,
            value: (input) => input.value,
            disabled: (input) => input.disabled ?? false,
            reportValidity: (input) => typeof input.reportValidity === 'function' ? input.reportValidity() : true,
            checkValidity: (input) => (typeof input.checkValidity === 'function' ? input.checkValidity() : true),
            setValue: (input, value) => {
                // eslint-disable-next-line no-param-reassign
                input.value = value;
            },
            assumeInteractionOn: ['it-input'],
            ...options,
        };
    }
    hostConnected() {
        const form = this.options.form(this.host);
        if (form) {
            this.attachForm(form);
        }
        // Listen for interactions
        interactions.set(this.host, []);
        this.options.assumeInteractionOn.forEach((event) => {
            this.host.addEventListener(event, this.handleInteraction);
        });
    }
    hostDisconnected() {
        this.detachForm();
        // Clean up interactions
        interactions.delete(this.host);
        this.options.assumeInteractionOn.forEach((event) => {
            this.host.removeEventListener(event, this.handleInteraction);
        });
    }
    hostUpdated() {
        const form = this.options.form(this.host);
        // Detach if the form no longer exists
        if (!form) {
            this.detachForm();
        }
        // If the form has changed, reattach it
        if (form && this.form !== form) {
            this.detachForm();
            this.attachForm(form);
        }
        if (this.host.hasUpdated) {
            this.setValidity(this.host.validity.valid);
        }
    }
    attachForm(form) {
        if (form) {
            this.form = form;
            // Add this element to the form's collection
            if (formCollections.has(this.form)) {
                formCollections.get(this.form).add(this.host);
            }
            else {
                formCollections.set(this.form, new Set([this.host]));
            }
            this.form.addEventListener('formdata', this.handleFormData);
            this.form.addEventListener('submit', this.handleFormSubmit);
            this.form.addEventListener('reset', this.handleFormReset);
            // Overload the form's reportValidity() method so it looks at FormControl
            if (!reportValidityOverloads.has(this.form)) {
                reportValidityOverloads.set(this.form, this.form.reportValidity);
                this.form.reportValidity = () => this.reportFormValidity();
            }
            // Overload the form's checkValidity() method so it looks at FormControl
            if (!checkValidityOverloads.has(this.form)) {
                checkValidityOverloads.set(this.form, this.form.checkValidity);
                this.form.checkValidity = () => this.checkFormValidity();
            }
        }
        else {
            this.form = undefined;
        }
    }
    detachForm() {
        if (!this.form)
            return;
        const formCollection = formCollections.get(this.form);
        if (!formCollection) {
            return;
        }
        this.submittedOnce = false;
        // Remove this host from the form's collection
        formCollection.delete(this.host);
        // Check to make sure there's no other form controls in the collection. If we do this
        // without checking if any other controls are still in the collection, then we will wipe out the
        // validity checks for all other elements.
        // see: https://github.com/shoelace-style/shoelace/issues/1703
        if (formCollection.size <= 0) {
            this.form.removeEventListener('formdata', this.handleFormData);
            this.form.removeEventListener('submit', this.handleFormSubmit);
            this.form.removeEventListener('reset', this.handleFormReset);
            // Remove the overload and restore the original method
            if (reportValidityOverloads.has(this.form)) {
                this.form.reportValidity = reportValidityOverloads.get(this.form);
                reportValidityOverloads.delete(this.form);
            }
            if (checkValidityOverloads.has(this.form)) {
                this.form.checkValidity = checkValidityOverloads.get(this.form);
                checkValidityOverloads.delete(this.form);
            }
            // So it looks weird here to not always set the form to undefined. But I _think_ if we unattach this.form here,
            // we end up in this fun spot where future validity checks don't have a reference to the form validity handler.
            // First form element in sets the validity handler. So we can't clean up `this.form` until there are no other form elements in the form.
            this.form = undefined;
        }
    }
    // eslint-disable-next-line class-methods-use-this
    setUserInteracted(el, hasInteracted) {
        if (hasInteracted) {
            userInteractedControls.add(el);
        }
        else {
            userInteractedControls.delete(el);
        }
        el.requestUpdate();
    }
    doAction(type, submitter) {
        // console.log('doaction', type);
        if (this.form) {
            const button = document.createElement('button');
            button.type = type;
            button.style.position = 'absolute';
            button.style.width = '0';
            button.style.height = '0';
            button.style.clipPath = 'inset(50%)';
            button.style.overflow = 'hidden';
            button.style.whiteSpace = 'nowrap';
            // Pass name, value, and form attributes through to the temporary button
            if (submitter) {
                button.name = submitter.name;
                button.value = submitter.value;
                ['formaction', 'formenctype', 'formmethod', 'formnovalidate', 'formtarget'].forEach((attr) => {
                    if (submitter.hasAttribute(attr)) {
                        button.setAttribute(attr, submitter.getAttribute(attr));
                    }
                });
            }
            this.form.append(button);
            button.click();
            button.remove();
        }
    }
    /** Returns the associated `<form>` element, if one exists. */
    getForm() {
        return this.form ?? null;
    }
    /** Resets the form, restoring all the control to their default value */
    reset(submitter) {
        this.doAction('reset', submitter);
    }
    /** Submits the form, triggering validation and form data injection. */
    submit(submitter) {
        // Calling form.submit() bypasses the submit event and constraint validation. To prevent this, we can inject a
        // native submit button into the form, "click" it, then remove it to simulate a standard form submission.
        this.doAction('submit', submitter);
    }
    /**
     * Synchronously sets the form control's validity. Call this when you know the future validity but need to update
     * the host element immediately, i.e. before Lit updates the component in the next update.
     */
    setValidity(isValid) {
        const host = this.host;
        const hasInteracted = Boolean(userInteractedControls.has(host));
        const required = Boolean(host.required);
        //
        // We're mapping the following "states" to data attributes. In the future, we can use ElementInternals.states to
        // create a similar mapping, but instead of [data-invalid] it will look like :--invalid.
        //
        // See this RFC for more details: https://github.com/shoelace-style/shoelace/issues/1011
        //
        host.toggleAttribute('data-required', required);
        host.toggleAttribute('data-optional', !required);
        host.toggleAttribute('data-invalid', !isValid);
        host.toggleAttribute('data-valid', isValid);
        host.toggleAttribute('data-user-invalid', !isValid && hasInteracted);
        host.toggleAttribute('data-user-valid', isValid && hasInteracted);
    }
    userInteracted() {
        const hasInteracted = Boolean(userInteractedControls.has(this.host));
        return hasInteracted;
    }
    /**
     * Updates the form control's validity based on the current value of `host.validity.valid`. Call this when anything
     * that affects constraint validation changes so the component receives the correct validity states.
     */
    updateValidity() {
        const host = this.host;
        this.setValidity(host.validity.valid);
    }
    /**
     * Dispatches a non-bubbling, cancelable custom event of type `it-invalid`.
     * If the `it-invalid` event will be cancelled then the original `invalid`
     * event (which may have been passed as argument) will also be cancelled.
     * If no original `invalid` event has been passed then the `it-invalid`
     * event will be cancelled before being dispatched.
     */
    emitInvalidEvent(originalInvalidEvent) {
        const itInvalidEvent = new CustomEvent('it-invalid', {
            bubbles: false,
            composed: false,
            cancelable: true,
            detail: {},
        });
        if (!originalInvalidEvent) {
            itInvalidEvent.preventDefault();
        }
        if (!this.host.dispatchEvent(itInvalidEvent)) {
            originalInvalidEvent?.preventDefault();
        }
    }
}

const translation = {
    $code: 'it',
    $name: 'Italiano',
    $dir: 'ltr',
    validityRequired: 'Questo campo è obbligatorio.',
    validityGroupRequired: "Scegli almeno un'opzione",
    validityPattern: 'Il valore non corrisponde al formato richiesto.',
    validityMinlength: 'Il valore deve essere lungo almeno {minlength} caratteri.',
    validityMaxlength: 'Il valore deve essere lungo al massimo {maxlength} caratteri.',
};

registerTranslation(translation);
class FormControl extends BaseLocalizedComponent {
    constructor() {
        super(...arguments);
        this.formControlController = new FormControlController(this, {
            assumeInteractionOn: ['it-input', 'it-blur', 'it-change'],
        });
        // TODO: verificare se serve davvero con il fatto che usiamo form-controller
        // static formAssociated = true;
        // @property()
        // internals = this.attachInternals();
        this._touched = false;
        /** The name of the input, submitted as a name/value pair with form data. */
        this.name = '';
        /** The current value of the input, submitted as a name/value pair with form data. */
        this.value = '';
        /** If the input is disabled. */
        this.disabled = false;
        /**
         * By default, form controls are associated with the nearest containing `<form>` element. This attribute allows you
         * to place the form control outside of a form and associate it with the form that has this `id`. The form must be in
         * the same document or shadow root for this to work.
         */
        this.form = '';
        /** If you implement your custom validation and you won't to trigger default validation */
        this.customValidation = false;
        /** If your input is invalid from your custom validition, set this attribute with message validation */
        this.validationText = '';
        /**
         * Specifies the granularity that the value must adhere to, or the special value `any` which means no stepping is
         * implied, allowing any numeric value. Only applies to date and number input types.
         */
        this.step = 'any';
        /** The input's minimum length. */
        this.minlength = -1;
        /** The input's maximum length. */
        this.maxlength = -1;
        /** If the input is required. */
        this.required = false;
        /* For grouped input, like checkbox-group */
        this.isInGroup = false;
        this.validationMessage = '';
    }
    /** Gets the validity state object */
    get validity() {
        return this.inputElement?.validity;
    }
    /** Gets the associated form, if one exists. */
    getForm() {
        return this.formControlController.getForm();
    }
    // Form validation methods
    checkValidity() {
        const inputValid = this.inputElement?.checkValidity() ?? true; // this.inputElement.checkValidity() è la validazione del browser
        return inputValid;
    }
    /** Checks for validity and shows the browser's validation message if the control is invalid. */
    reportValidity() {
        // const ret = this.inputElement.checkValidity();
        const ret = this.checkValidity(); // chiama la checkValidity, che se è stata overridata chiama quella
        this.handleValidationMessages();
        return ret; // this.inputElement.reportValidity();
    }
    /** Sets a custom validation message. Pass an empty string to restore validity. */
    setCustomValidity(message) {
        this.inputElement.setCustomValidity(message);
        this.validationMessage = this.inputElement.validationMessage;
        this.formControlController.updateValidity();
    }
    // Handlers
    _handleReady() {
        requestAnimationFrame(() => {
            this.dispatchEvent(new CustomEvent('it-input-ready', { bubbles: true, detail: { el: this.inputElement } }));
        });
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleInput(e) {
        this.handleValidationMessages();
        this.dispatchEvent(new CustomEvent('it-input', {
            detail: { value: this.inputElement.value, el: this.inputElement },
            bubbles: true,
            composed: true,
        }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleBlur(e) {
        this._touched = true;
        this.handleValidationMessages();
        this.dispatchEvent(new FocusEvent('it-blur', { bubbles: true, composed: true }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleFocus(e) {
        this.dispatchEvent(new FocusEvent('it-focus', { bubbles: true, composed: true }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleClick(e) {
        this.dispatchEvent(new MouseEvent('it-click', { bubbles: true, composed: true }));
    }
    /*
    Override default browser validation messages
     */
    handleValidationMessages() {
        if (!this.customValidation) {
            const _v = this.inputElement.validity;
            const isRequiredHandledByGroup = this.isInGroup === true;
            if (_v.valueMissing && !isRequiredHandledByGroup) {
                this.setCustomValidity(this.$t('validityRequired'));
            }
            else if (_v.patternMismatch) {
                this.setCustomValidity(this.$t('validityPattern'));
            }
            else if (_v.tooShort) {
                this.setCustomValidity(this.$t('validityMinlength').replace('{minlength}', this.minlength.toString()));
            }
            else if (_v.tooLong) {
                this.setCustomValidity(this.$t('validityMaxlength').replace('{maxlength}', this.maxlength.toString()));
            }
            else {
                /* nothing. Usa il messaggio di errore della validazione
                di default del browser per altri errori di validità come:
                - typeMismatch
                - rangeUnderflow
                - rangeOverflow
                - stepMismatch
                - badInput */
                const otherConstraintErrors = _v.typeMismatch || _v.rangeUnderflow || _v.rangeOverflow || _v.stepMismatch || _v.badInput;
                if (!otherConstraintErrors) {
                    this.setCustomValidity('');
                }
            }
        }
        this.validationMessage = this.inputElement.validationMessage;
    }
    _handleInvalid(event) {
        this.formControlController.setValidity(false);
        this.formControlController.emitInvalidEvent(event);
    }
    _handleChange(e) {
        const target = e.target;
        let value;
        if (target instanceof HTMLInputElement) {
            switch (target.type) {
                case 'checkbox':
                case 'radio':
                    value = target.checked;
                    break;
                case 'file':
                    value = target.files; // FileList
                    break;
                default:
                    value = target.value;
            }
        }
        else if (target instanceof HTMLSelectElement) {
            if (target.multiple) {
                value = Array.from(target.selectedOptions).map((o) => o.value);
            }
            else {
                value = target.value;
            }
        }
        else {
            // textarea o altri input con value
            value = target.value;
        }
        this.dispatchEvent(new CustomEvent('it-change', {
            detail: { value, el: target },
            bubbles: true,
            composed: true,
        }));
    }
    updated(changedProperties) {
        super.updated?.(changedProperties);
        if (this.customValidation) {
            this.setCustomValidity(this.validationText ?? '');
        }
        else if (this.formControlController.userInteracted()) {
            this.formControlController.updateValidity();
        }
    }
}
__decorate([
    state(),
    __metadata("design:type", Object)
], FormControl.prototype, "_touched", void 0);
__decorate([
    query('.it-form__control'),
    __metadata("design:type", HTMLInputElement)
], FormControl.prototype, "inputElement", void 0);
__decorate([
    property({ type: String, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "name", void 0);
__decorate([
    property({ reflect: true }),
    __metadata("design:type", Object)
], FormControl.prototype, "value", void 0);
__decorate([
    property({ type: Boolean, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "disabled", void 0);
__decorate([
    property({ reflect: true, type: String }),
    __metadata("design:type", Object)
], FormControl.prototype, "form", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'custom-validation' }),
    __metadata("design:type", Object)
], FormControl.prototype, "customValidation", void 0);
__decorate([
    property({ attribute: 'validity-message', reflect: true }),
    __metadata("design:type", String)
], FormControl.prototype, "validationText", void 0);
__decorate([
    property(),
    __metadata("design:type", String)
], FormControl.prototype, "pattern", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "min", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "max", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "step", void 0);
__decorate([
    property({ type: Number }),
    __metadata("design:type", Object)
], FormControl.prototype, "minlength", void 0);
__decorate([
    property({ type: Number }),
    __metadata("design:type", Object)
], FormControl.prototype, "maxlength", void 0);
__decorate([
    property({ type: Boolean, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "required", void 0);
__decorate([
    property({ type: Boolean }),
    __metadata("design:type", Object)
], FormControl.prototype, "isInGroup", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], FormControl.prototype, "validationMessage", void 0);

/**
 * Dispatches a cancelable custom event and runs the provided default action
 * only if the consumer did not call `event.preventDefault()`.
 *
 * This is the shared convention used across components (Tabs, Modal, Chip, Alert,
 * Forward, Back) to provide a sensible default JavaScript behavior out of the box
 * while letting consumers intercept and override it. Notification deliberately does
 * NOT use this: its show/close are always caller-initiated (no default markup-driven
 * trigger to override), so there was no real use case for blocking them — see
 * it-notification.ts for the plain (non-cancelable) events it fires instead.
 *
 * ```js
 * el.addEventListener('it-alert-close', (e) => {
 *   e.preventDefault();                    // block the default removal
 *   fetchConfirmation().then((ok) => {     // async logic runs after prevention
 *     if (ok) e.target.close();
 *   });
 * });
 * ```
 *
 * Note: `preventDefault()` must be called synchronously inside the listener.
 * Async code can run afterward; call the component's public method when ready.
 *
 * @param host the element dispatching the event
 * @param name the event name (kebab-case, `it-` prefixed)
 * @param detail the event `detail` payload
 * @param defaultAction the behavior executed when the event is not prevented
 * @returns the dispatched event (inspect `defaultPrevented` if needed)
 */
function dispatchCancelable(host, name, detail, defaultAction) {
    const event = new CustomEvent(name, {
        bubbles: true,
        composed: true,
        cancelable: true,
        detail,
    });
    host.dispatchEvent(event);
    if (!event.defaultPrevented) {
        defaultAction();
    }
    return event;
}

class WindowManager {
    static init() {
        if (this.initialized)
            return;
        this.lastScrollY = window.scrollY;
        this.lastWidth = window.innerWidth;
        this.lastHeight = window.innerHeight;
        window.addEventListener('scroll', () => {
            this.lastScrollY = window.scrollY;
            this.requestTick();
        });
        window.addEventListener('resize', () => {
            this.lastWidth = window.innerWidth;
            this.lastHeight = window.innerHeight;
            this.requestTick(true); // force recalculation
        });
        this.initialized = true;
    }
    static requestTick(forceRecalc = false) {
        if (!this.ticking) {
            requestAnimationFrame(() => this.tick(forceRecalc));
            this.ticking = true;
        }
    }
    static tick(forceRecalc) {
        this.ticking = false;
        const state = {
            scrollY: this.lastScrollY,
            width: this.lastWidth,
            height: this.lastHeight,
        };
        this.subscribers.forEach((cb) => cb(state, forceRecalc));
    }
    static subscribe(cb) {
        this.subscribers.add(cb);
    }
    static unsubscribe(cb) {
        this.subscribers.delete(cb);
    }
    /**
     * Blocca lo scroll del body. Usa ref counting per gestire modal nested.
     * Chiamare unlockBodyScroll() per ogni lockBodyScroll().
     */
    static lockBodyScroll() {
        this.scrollLockCount += 1;
        if (this.scrollLockCount === 1) {
            this.scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.body.classList.add('modal-open');
            document.body.style.overflow = 'hidden';
            document.body.style.paddingRight = `${this.scrollbarWidth}px`;
        }
    }
    /**
     * Sblocca lo scroll del body. Rimuove il lock solo quando tutti i consumer hanno chiamato unlock.
     */
    static unlockBodyScroll() {
        this.scrollLockCount = Math.max(0, this.scrollLockCount - 1);
        if (this.scrollLockCount === 0) {
            document.body.classList.remove('modal-open');
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        }
    }
    /** Ritorna la larghezza della scrollbar (utile per compensazioni) */
    static getScrollbarWidth() {
        return this.scrollbarWidth || window.innerWidth - document.documentElement.clientWidth;
    }
}
WindowManager.initialized = false;
WindowManager.subscribers = new Set();
WindowManager.lastScrollY = 0;
WindowManager.lastWidth = 0;
WindowManager.lastHeight = 0;
WindowManager.ticking = false;
// Body scroll lock (ref counting per modal nested)
WindowManager.scrollLockCount = 0;
WindowManager.scrollbarWidth = 0;

if (typeof window !== 'undefined') {
    window._itAnalytics = window._itAnalytics || {};
    window._itAnalytics = {
        ...window._itAnalytics,
        version: '1.0.0-alpha.19',
    };
}

var styles = css`@charset "UTF-8";
.modal {
  --bsi-modal-bg: var(--bsi-color-background-inverse);
  --bsi-modal-border-color: transparent;
  --bsi-modal-border-radius: var(--bsi-radius-smooth);
  --bsi-modal-border-width: 0;
  --bsi-modal-box-shadow: var(--bsi-elevation-medium);
  --bsi-modal-footer-bg: transparent;
  --bsi-modal-footer-border-color: var(--bsi-modal-header-border-color);
  --bsi-modal-footer-border-width: var(--bsi-modal-header-border-width);
  --bsi-modal-footer-gap: var(--bsi-spacing-s);
  --bsi-modal-header-border-color: var(--bsi-color-border-subtle);
  --bsi-modal-header-border-width: 0;
  --bsi-modal-inner-border-radius: var(--bsi-radius-smooth);
  --bsi-modal-margin: auto;
  --bsi-modal-padding: var(--bsi-spacing-m);
  --bsi-modal-title-font-size: var(--bsi-heading-5-font-size);
  --bsi-modal-title-font-weight: var(--bsi-heading-font-weight-weak);
  --bsi-modal-title-line-height: var(--bsi-heading-line-height);
  --bsi-modal-transition: transform 0.3s ease-in-out;
  --bsi-modal-width: 500px;
  --bsi-modal-zindex: 1055;
}

.modal-dialog {
  position: relative;
  width: auto;
  margin: var(--bsi-modal-margin);
  pointer-events: none;
}
.modal.show .modal-dialog {
  transform: none;
}
.modal.modal-static .modal-dialog {
  transform: scale(1.02);
}

.modal-dialog-scrollable {
  height: calc(100% - var(--bsi-modal-margin) * 2);
}
.modal-dialog-scrollable .modal-content {
  max-height: 100%;
  overflow: hidden;
}
.modal-dialog-scrollable .modal-body {
  overflow-y: auto;
}

.modal-dialog-centered {
  display: flex;
  align-items: center;
  min-height: calc(100% - var(--bsi-modal-margin) * 2);
}

.modal-body {
  position: relative;
  flex: 1 1 auto;
  padding: var(--bsi-modal-padding);
  color: var(--bsi-color-text-base);
}

.modal-content {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  border: var(--bsi-modal-border-width) solid var(--bsi-modal-border-color);
  outline: 0;
  background-color: var(--bsi-modal-bg);
  pointer-events: auto;
  background-clip: padding-box;
  border-radius: var(--bsi-modal-border-radius);
}

.modal-backdrop {
  --bsi-backdrop-zindex: 1050;
  --bsi-backdrop-bg: hsl(0, 0%, 0%);
  --bsi-backdrop-opacity: 0.8;
  position: fixed;
  top: 0;
  left: 0;
  z-index: var(--bsi-backdrop-zindex);
  width: 100vw;
  height: 100vh;
  background-color: var(--bsi-backdrop-bg);
}
.modal-backdrop.fade {
  opacity: 0;
}
.modal-backdrop.show {
  opacity: var(--bsi-backdrop-opacity);
}

.modal-header {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  padding: var(--bsi-modal-padding) var(--bsi-modal-padding) 0;
  border-bottom: var(--bsi-modal-header-border-width) solid var(--bsi-modal-header-border-color);
}

.modal-footer {
  position: relative;
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: var(--bsi-modal-footer-gap);
  align-items: center;
  justify-content: flex-end;
  padding: 0 var(--bsi-modal-padding) var(--bsi-modal-padding);
  border-top: var(--bsi-modal-footer-border-width) solid var(--bsi-modal-footer-border-color);
  background-color: var(--bsi-modal-footer-bg);
  border-bottom-right-radius: var(--bsi-modal-border-radius);
  border-bottom-left-radius: var(--bsi-modal-border-radius);
}
.modal-footer.modal-footer-shadow {
  box-shadow: 0 15px 25px 5px rgba(0, 0, 0, 0.3);
}

.modal-title {
  margin-bottom: 0;
  font-size: var(--bsi-modal-title-font-size);
  font-weight: var(--bsi-modal-title-font-weight);
  line-height: var(--bsi-modal-title-line-height);
}

@media (min-width: 576px) {
  .modal {
    --bsi-modal-margin: 1.5rem;
    --bsi-modal-box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  }
  .modal-dialog {
    max-width: var(--bsi-modal-width);
  }
  .modal-sm {
    --bsi-modal-width: 300px;
  }
}
@media (min-width: 992px) {
  .modal-lg,
  .modal-xl {
    --bsi-modal-width: 800px;
  }
}
@media (min-width: 1200px) {
  .modal-xl {
    --bsi-modal-width: 1140px;
  }
}
.modal-fullscreen {
  width: 100vw;
  max-width: none;
  height: 100%;
  margin: 0;
}
.modal-fullscreen .modal-content {
  height: 100%;
  border: 0;
  border-radius: 0;
}
.modal-fullscreen .modal-header,
.modal-fullscreen .modal-footer {
  border-radius: 0;
}
.modal-fullscreen .modal-body {
  overflow-y: auto;
}

@media (max-width: 575.98px) {
  .modal-fullscreen-sm-down {
    width: 100vw;
    max-width: none;
    height: 100%;
    margin: 0;
  }
  .modal-fullscreen-sm-down .modal-content {
    height: 100%;
    border: 0;
    border-radius: 0;
  }
  .modal-fullscreen-sm-down .modal-header,
  .modal-fullscreen-sm-down .modal-footer {
    border-radius: 0;
  }
  .modal-fullscreen-sm-down .modal-body {
    overflow-y: auto;
  }
}
@media (max-width: 767.98px) {
  .modal-fullscreen-md-down {
    width: 100vw;
    max-width: none;
    height: 100%;
    margin: 0;
  }
  .modal-fullscreen-md-down .modal-content {
    height: 100%;
    border: 0;
    border-radius: 0;
  }
  .modal-fullscreen-md-down .modal-header,
  .modal-fullscreen-md-down .modal-footer {
    border-radius: 0;
  }
  .modal-fullscreen-md-down .modal-body {
    overflow-y: auto;
  }
}
@media (max-width: 991.98px) {
  .modal-fullscreen-lg-down {
    width: 100vw;
    max-width: none;
    height: 100%;
    margin: 0;
  }
  .modal-fullscreen-lg-down .modal-content {
    height: 100%;
    border: 0;
    border-radius: 0;
  }
  .modal-fullscreen-lg-down .modal-header,
  .modal-fullscreen-lg-down .modal-footer {
    border-radius: 0;
  }
  .modal-fullscreen-lg-down .modal-body {
    overflow-y: auto;
  }
}
@media (max-width: 1199.98px) {
  .modal-fullscreen-xl-down {
    width: 100vw;
    max-width: none;
    height: 100%;
    margin: 0;
  }
  .modal-fullscreen-xl-down .modal-content {
    height: 100%;
    border: 0;
    border-radius: 0;
  }
  .modal-fullscreen-xl-down .modal-header,
  .modal-fullscreen-xl-down .modal-footer {
    border-radius: 0;
  }
  .modal-fullscreen-xl-down .modal-body {
    overflow-y: auto;
  }
}
@media (max-width: 1399.98px) {
  .modal-fullscreen-xxl-down {
    width: 100vw;
    max-width: none;
    height: 100%;
    margin: 0;
  }
  .modal-fullscreen-xxl-down .modal-content {
    height: 100%;
    border: 0;
    border-radius: 0;
  }
  .modal-fullscreen-xxl-down .modal-header,
  .modal-fullscreen-xxl-down .modal-footer {
    border-radius: 0;
  }
  .modal-fullscreen-xxl-down .modal-body {
    overflow-y: auto;
  }
}
/* stylelint-disable-next-line no-duplicate-selectors */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  z-index: var(--bsi-modal-zindex);
  display: none;
  width: 100%;
  height: 100%;
  padding-right: 0 !important;
  overflow-x: hidden;
  overflow-y: auto;
  outline: 0;
}
.modal.alert-modal .modal-dialog .modal-content .modal-header {
  align-items: start;
  justify-content: left;
}
.modal.alert-modal .modal-dialog .modal-content .modal-header .icon {
  flex-shrink: 0;
  margin-right: 16px;
  fill: hsl(210, 100%, 40%);
}
.modal.it-dialog-link-list .modal-dialog .modal-content .modal-header {
  --bsi-modal-header-border-color: var(--bsi-color-border-subtle);
  --bsi-modal-header-border-width: 1px;
  padding-bottom: var(--bsi-modal-padding);
}
.modal.it-dialog-link-list .modal-dialog .modal-content .modal-body .link-list-wrapper .link-list {
  margin-bottom: 0;
  padding-bottom: var(--bsi-modal-padding);
}
.modal.popconfirm-modal .modal-dialog {
  margin-right: auto;
  margin-left: auto;
}
.modal.popconfirm-modal .modal-dialog .modal-body p {
  font-size: var(--bsi-caption-font-size);
  line-height: var(--bsi-caption-leading);
}
.modal.popconfirm-modal .modal-dialog .modal-footer {
  justify-content: flex-start;
}
.modal.it-dialog-scrollable .modal-dialog .modal-content {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 208px);
  border-radius: 0;
}
.modal.it-dialog-scrollable .modal-dialog .modal-content .modal-header {
  flex-shrink: 0;
  padding-bottom: var(--bsi-modal-padding);
}
.modal.it-dialog-scrollable .modal-dialog .modal-content .modal-body {
  padding-top: 0;
  overflow-y: auto;
}
.modal.it-dialog-scrollable .modal-dialog .modal-content .modal-footer {
  padding-top: var(--bsi-modal-padding);
  flex-shrink: 0;
}
.modal.it-dialog-scrollable .modal-dialog.modal-dialog-left {
  height: 100vh;
  margin: 0 var(--bsi-spacing-m) 0 0;
}
.modal.it-dialog-scrollable .modal-dialog.modal-dialog-left .modal-content {
  height: 100vh;
}
.modal.it-dialog-scrollable .modal-dialog.modal-dialog-right {
  height: 100vh;
  margin: 0 0 0 var(--bsi-spacing-m);
  float: right;
}
.modal.it-dialog-scrollable .modal-dialog.modal-dialog-right .modal-content {
  height: 100vh;
}
.modal.fade .modal-dialog {
  transition: var(--bsi-modal-transition);
}
@media (prefers-reduced-motion: reduce) {
  .modal.fade .modal-dialog {
    transition: none;
  }
}
.modal.fade .modal-dialog.modal-dialog-left {
  transform: translateX(-100%);
  transition: transform 0.3s ease-in-out;
}
.modal.fade .modal-dialog.modal-dialog-right {
  transform: translateX(100%);
  transition: transform 0.3s ease-in-out;
}
.modal.fade .modal-dialog.modal-dialog-centered {
  transform: translate(0, -5%);
}
.modal.fade.show .modal-dialog.modal-dialog-left {
  transform: translateX(0);
}
.modal.fade.show .modal-dialog.modal-dialog-right {
  transform: translateX(0);
}
.modal.fade.show .modal-dialog.modal-dialog-centered {
  transform: translate(0, 0);
}

@media (min-width: 768px) {
  .modal .modal-dialog {
    margin-right: auto;
    margin-left: auto;
  }
  .modal .modal-dialog.modal-dialog-left {
    margin: 0;
  }
  .modal .modal-dialog.modal-dialog-left .modal-content {
    height: 100vh;
  }
  .modal .modal-dialog.modal-dialog-right {
    margin: 0;
    float: right;
  }
  .modal .modal-dialog.modal-dialog-right .modal-content {
    height: 100vh;
  }
  .modal.it-dialog-scrollable .modal-dialog {
    margin: 64px auto;
  }
  .modal.it-dialog-scrollable .modal-dialog .modal-content {
    height: calc(100vh - 128px);
  }
}
*,
*::before,
*::after {
  box-sizing: border-box;
}

@media (prefers-reduced-motion: no-preference) {
  :root {
    scroll-behavior: smooth;
  }
}

body {
  margin: 0;
  -webkit-text-size-adjust: 100%;
  -webkit-tap-highlight-color: hsla(0, 0%, 0%, 0);
}

hr {
  margin: 1rem 0;
  color: inherit;
  border: 0;
  border-top: 1px solid;
  opacity: 0.25;
}

p {
  margin-top: 0;
  margin-bottom: var(--bsi-paragraph-spacing);
}

abbr[title] {
  text-decoration: underline dotted;
  cursor: help;
  text-decoration-skip-ink: none;
}

address {
  margin-bottom: var(--bsi-spacing-s);
  font-style: normal;
  line-height: inherit;
}

ol,
ul {
  padding-left: var(--bsi-spacing-l);
}

ol,
ul,
dl {
  margin-top: 0;
  margin-bottom: var(--bsi-spacing-s);
}

ol ol,
ul ul,
ol ul,
ul ol {
  margin-bottom: 0;
}

dt {
  font-weight: var(--bsi-font-weight-strong);
}

dd {
  margin-bottom: var(--bsi-spacing-xxs);
  margin-left: 0;
}

blockquote {
  margin: 0 0 var(--bsi-spacing-s);
}

sub,
sup {
  position: relative;
  font-size: var(--bsi-font-size-1);
  line-height: 0;
  vertical-align: baseline;
}

sub {
  bottom: -0.25em;
}

sup {
  top: -0.5em;
}

a {
  color: var(--bsi-color-link);
  text-decoration: underline;
  text-decoration-skip-ink: auto;
  text-underline-offset: 2px;
}
a:hover {
  color: var(--bsi-color-link-hover);
  cursor: pointer;
}

pre,
code,
kbd,
samp {
  font-size: 1em;
}

pre {
  display: block;
  margin-top: 0;
  margin-bottom: var(--bsi-paragraph-spacing);
  overflow: auto;
}
pre code {
  word-break: normal;
}

a > code {
  color: inherit;
}

figure {
  margin: 0 0 1rem;
}

img,
svg {
  vertical-align: middle;
}

table {
  caption-side: bottom;
  border-collapse: collapse;
}

caption {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  color: hsl(210, 17%, 44%);
  text-align: left;
}

th {
  text-align: inherit;
  text-align: -webkit-match-parent;
}

thead,
tbody,
tfoot,
tr,
td,
th {
  border-color: inherit;
  border-style: solid;
  border-width: 0;
}

label {
  display: inline-block;
}

button {
  border-radius: 0;
}

button:focus:not(:focus-visible) {
  outline: 0;
}

input,
button,
select,
optgroup,
textarea {
  margin: 0;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

button,
select {
  text-transform: none;
}

[role=button] {
  cursor: pointer;
}

select {
  word-wrap: normal;
}
select:disabled {
  opacity: 1;
}

[list]:not([type=date]):not([type=datetime-local]):not([type=month]):not([type=week]):not([type=time])::-webkit-calendar-picker-indicator {
  display: none !important;
}

button,
[type=button],
[type=reset],
[type=submit] {
  -webkit-appearance: button;
}
button:not(:disabled),
[type=button]:not(:disabled),
[type=reset]:not(:disabled),
[type=submit]:not(:disabled) {
  cursor: pointer;
}

::-moz-focus-inner {
  padding: 0;
  border-style: none;
}

textarea {
  resize: vertical;
}

fieldset {
  min-width: 0;
  padding: 0;
  margin: 0;
  border: 0;
}

::-webkit-datetime-edit-fields-wrapper,
::-webkit-datetime-edit-text,
::-webkit-datetime-edit-minute,
::-webkit-datetime-edit-hour-field,
::-webkit-datetime-edit-day-field,
::-webkit-datetime-edit-month-field,
::-webkit-datetime-edit-year-field {
  padding: 0;
}

::-webkit-inner-spin-button {
  height: auto;
}

[type=search] {
  outline-offset: -2px;
  -webkit-appearance: textfield;
}

/* rtl:raw:
[type="tel"],
[type="url"],
[type="email"],
[type="number"] {
  direction: ltr;
}
*/
::-webkit-search-decoration {
  -webkit-appearance: none;
}

::-webkit-color-swatch-wrapper {
  padding: 0;
}

::file-selector-button {
  font: inherit;
  -webkit-appearance: button;
}

output {
  display: inline-block;
}

iframe {
  border: 0;
}

summary {
  display: list-item;
  cursor: pointer;
}

progress {
  vertical-align: baseline;
}

[hidden] {
  display: none !important;
}

body {
  font-family: var(--bsi-font-sans);
}

p,
ul,
ol,
dl {
  font-size: var(--bsi-body-font-size);
  line-height: var(--bsi-body-leading);
}

caption,
figcaption {
  font-size: var(--bsi-caption-font-size);
  line-height: var(--bsi-caption-leading);
}

b,
strong {
  font-weight: var(--bsi-font-weight-strong);
}

mark,
code {
  padding: 0 var(--bsi-spacing-3xs);
}

mark {
  background-color: var(--bsi-highlight-background);
}

pre,
code,
kbd,
samp {
  border-radius: var(--bsi-radius-smooth);
  font-family: var(--bsi-font-mono);
}

ins,
del {
  position: relative;
  display: flex;
  align-items: center;
  padding: var(--bsi-spacing-3xs) var(--bsi-spacing-m);
  font: var(--bsi-body-font-size);
  text-decoration: none;
}

ins {
  background-color: var(--bsi-ins-background);
  text-decoration: none;
}

del {
  background-color: var(--bsi-del-background);
}

del::before,
ins::before {
  position: absolute;
  left: 0.5rem;
}

del::before {
  content: "−";
}

ins::before {
  content: "+";
}

kbd {
  margin: 0 2px;
  padding: 2px var(--bsi-spacing-3xs);
  border: var(--bsi-border-base) solid var(--bsi-color-border-subtle);
  background-color: var(--bsi-color-background-secondary-lighter);
  box-shadow: var(--bsi-elevation-low), 0 2px 0 0 hsla(255, 0%, 100%, 0.7) inset;
  color: var(--bsi-color-text-secondary);
  font-size: var(--bsi-font-size-1);
  font-weight: var(--bsi-font-weight-solid);
  white-space: nowrap;
}
kbd kbd {
  padding: 0;
  font-size: var(--bsi-code-font-size);
}

small,
.small {
  font-size: var(--bsi-caption-font-size);
}

.x-small {
  font-size: var(--bsi-font-size-1);
}

h1,
.h1 {
  font-size: var(--bsi-heading-1-font-size);
}

h2,
.h2 {
  font-size: var(--bsi-heading-2-font-size);
}

h3,
.h3 {
  font-size: var(--bsi-heading-3-font-size);
}

h4,
.h4 {
  font-size: var(--bsi-heading-4-font-size);
}

h5,
.h5 {
  font-size: var(--bsi-heading-5-font-size);
}

h6,
.h6 {
  font-size: var(--bsi-heading-6-font-size);
}

h1,
.h1,
h2,
.h2,
h3,
.h3,
h4,
.h4,
h5,
.h5,
h6,
.h6 {
  margin-top: 0;
  margin-bottom: var(--bsi-heading-spacing);
  line-height: var(--bsi-heading-leading);
}
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + h1, p + h1,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + .h1,
p + .h1,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + h2,
p + h2,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + .h2,
p + .h2,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + h3,
p + h3,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + .h3,
p + .h3,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + h4,
p + h4,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + .h4,
p + .h4,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + h5,
p + h5,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + .h5,
p + .h5,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + h6,
p + h6,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + .h6,
p + .h6 {
  padding-top: var(--bsi-spacing-s);
}

h1,
h2,
h3,
.h1,
.h2,
.h3 {
  font-weight: var(--bsi-heading-font-weight);
}

h4,
h5,
h6,
.h4,
.h5,
.h6 {
  font-weight: var(--bsi-heading-font-weight-weak);
}

h1,
.h1,
.display-1 {
  letter-spacing: -1px;
}

.font-serif {
  font-family: var(--bsi-font-serif) !important;
}

.font-sans-serif {
  font-family: var(--bsi-font-sans) !important;
}

.font-monospace {
  font-family: var(--bsi-font-mono) !important;
}

.display-1 {
  font-size: var(--bsi-display-font-size);
  font-weight: var(--bsi-heading-font-weight);
  line-height: var(--bsi-heading-leading);
}

.lead {
  font-size: var(--bsi-lead-font-size);
  font-weight: var(--bsi-lead-font-weight);
  line-height: var(--bsi-lead-leading);
}

blockquote,
.blockquote {
  margin: var(--bsi-spacing-m) 0;
  margin-left: var(--bsi-spacin-inline-xs);
  padding: var(--bsi-spacing-s);
  border-left: var(--bsi-border-thick) solid var(--bsi-border-color-secondary);
  font-size: var(--bsi-body-font-size);
  line-height: var(--bsi-body-leading);
}
blockquote > :last-child,
.blockquote > :last-child {
  margin-bottom: 0;
}
blockquote.text-end,
.blockquote.text-end {
  margin-right: var(--bsi-spacin-inline-xs);
  border-right: var(--bsi-border-thick) solid var(--bsi-border-color-secondary);
}
blockquote.text-center, blockquote.text-end, blockquote.blockquote-simple,
.blockquote.text-center,
.blockquote.text-end,
.blockquote.blockquote-simple {
  margin-left: 0;
  border-right: none;
  border-left: none;
}
blockquote.text-center, blockquote.blockquote-simple,
.blockquote.text-center,
.blockquote.blockquote-simple {
  padding: 0;
}
blockquote.blockquote-simple,
.blockquote.blockquote-simple {
  font-style: italic;
}
blockquote.blockquote-card,
.blockquote.blockquote-card {
  margin-left: 0;
  padding: var(--bsi-spacing-m);
  background-color: var(--bsi-color-background-inverse);
  box-shadow: var(--bsi-elevation-low);
}
blockquote.blockquote-card .blockquote-footer,
.blockquote.blockquote-card .blockquote-footer {
  font-size: inherit;
}
blockquote.blockquote-card .blockquote-footer:before,
.blockquote.blockquote-card .blockquote-footer:before {
  content: none;
}
blockquote.blockquote-card.dark,
.blockquote.blockquote-card.dark {
  border-left: none;
  background-color: var(--bsi-color-background-primary);
  color: var(--bsi-color-text-inverse);
}
blockquote.blockquote-card.dark .blockquote-footer,
.blockquote.blockquote-card.dark .blockquote-footer {
  color: var(--bsi-color-text-inverse);
}

.blockquote-footer {
  margin-top: 0;
  margin-bottom: var(--bsi-spacing-s);
  color: var(--bsi-color-text-secondary);
  font-size: var(--bsi-label-font-size);
}
.blockquote-footer::before {
  content: "— ";
}
.bg-dark .blockquote-footer {
  color: var(--bsi-color-text-muted);
}

.initialism {
  font-size: var(--bsi-label-font-size-s);
  text-transform: uppercase;
}

.visually-hidden,
.visually-hidden-focusable:not(:focus):not(:focus-within) {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

.d-none {
  display: none !important;
}

.d-block {
  display: block !important;
}

.d-flex {
  display: flex !important;
}

.overflow-auto {
  overflow: auto !important;
}

.overflow-hidden {
  overflow: hidden !important;
}

@media (min-width: 576px) {
  .d-sm-none {
    display: none !important;
  }
  .d-sm-block {
    display: block !important;
  }
  .d-sm-flex {
    display: flex !important;
  }
}
@media (min-width: 768px) {
  .d-md-none {
    display: none !important;
  }
  .d-md-block {
    display: block !important;
  }
  .d-md-flex {
    display: flex !important;
  }
}
@media (min-width: 992px) {
  .d-lg-none {
    display: none !important;
  }
  .d-lg-block {
    display: block !important;
  }
  .d-lg-flex {
    display: flex !important;
  }
}
@media (min-width: 1200px) {
  .d-xl-none {
    display: none !important;
  }
  .d-xl-block {
    display: block !important;
  }
  .d-xl-flex {
    display: flex !important;
  }
}
@media (min-width: 1400px) {
  .d-xxl-none {
    display: none !important;
  }
  .d-xxl-block {
    display: block !important;
  }
  .d-xxl-flex {
    display: flex !important;
  }
}
@media print {
  .d-print-none {
    display: none !important;
  }
  .d-print-block {
    display: block !important;
  }
  .d-print-flex {
    display: flex !important;
  }
}
:host {
  display: block;
  --it-focus-ring-safari-fix: none;
}

/* Rimuovi il flash visivo solo durante l'apertura programmata */
[part=modal]:focus {
  box-shadow: var(--it-focus-ring-safari-fix) !important;
  outline: var(--it-focus-ring-safari-fix) !important;
}

[role=dialog]:focus,
[role=dialog]:focus-visible,
.btn-close:focus:not(:focus-visible) {
  box-shadow: none !important;
  outline: 0 !important;
}

.btn-close:focus-visible {
  outline: 2px solid var(--it-focus-color) !important;
}

:host(:not([open])) .modal,
:host(:not([open])) .modal-backdrop {
  display: none !important;
}

.modal-backdrop {
  position: fixed;
  z-index: 1050;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: hsl(0, 0%, 0%);
}

.modal {
  position: fixed;
  z-index: 1055;
  top: 0;
  left: 0;
  display: flex;
  width: 100%;
  height: 100%;
  outline: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

:host([close-button-placement=backdrop]) .btn-close[part~=close-button] {
  --bsi-icon-default: white;
  position: absolute;
  top: 1rem;
  right: -2.5rem;
}

.it-dialog-scrollable {
  overflow-y: hidden;
}

.modal-dialog {
  height: fit-content;
}
@media (min-width: 768px) {
  .modal-dialog {
    margin: 64px auto;
  }
}

.modal-footer {
  flex-shrink: 0;
  padding-top: var(--bsi-modal-padding);
}

.modal-dialog-scrollable {
  height: unset;
}

.modal-dialog-centered {
  min-height: unset;
  margin: auto;
}

.modal.alert-modal .modal-dialog .modal-content .modal-header {
  align-items: center;
  justify-content: space-between;
  gap: var(--it-spacing-2x);
}

.modal.it-dialog-scrollable .modal-dialog.modal-dialog-right {
  position: fixed;
  right: 0;
}`;

/**
 * Componente Modal per finestre di dialogo modali.
 *
 * @element it-modal
 *
 * @slot trigger - Elemento che apre la modale (es. it-button)
 * @slot content - Contenuto principale della modale (body)
 * @slot header-icon - Icona opzionale nell'header
 * @slot footer - Azioni del footer (pulsanti)
 *
 * @fires it-modal-open - Quando la modale si apre (cancelable: `preventDefault()` impedisce l'apertura)
 * @fires it-modal-close - Quando la modale si chiude (cancelable: `preventDefault()` impedisce la chiusura)
 *
 * @prop {string} close-button-placement - Posizione del pulsante di chiusura: `header` (default) o `backdrop` (sopra lo sfondo scuro, utile per menu offcanvas)
 */
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
let ItModal = class ItModal extends BaseComponent {
    get _triggerElement() {
        const elements = this._triggerSlot?.assignedElements({ flatten: true });
        return elements?.[0] || null;
    }
    constructor() {
        super();
        this.open = undefined;
        this.modalTitle = '';
        this.modalDescription = '';
        this.customHeader = false;
        this.size = '';
        this.scrollable = false;
        this.staticBackdrop = false;
        this.hideCloseButton = false;
        this.closeButtonPlacement = 'header';
        this.itAriaLabel = '';
        this.closeLabel = '';
        this.disableAnimation = false;
        this.footerShadow = false;
        this._titleId = '';
        this._triggerId = '';
        this._descriptionId = '';
        this.isAnimating = false;
        this._isSelfClosing = false;
        this._originalTrigger = null;
        this._safariMouseInteraction = false;
        this._dialogAnimationDuration = 300;
        this._backdropAnimationDuration = 150;
        this._inertElements = [];
        this._triggerPointerBefore = null;
        /** Timer ID for the focus-restore call in finishHide. Cancelled on reconnect/unmount
         *  to prevent a stale focus from a previous close cycle firing during a new open. */
        this._restoreFocusTimer = null;
        // eslint-disable-next-line class-methods-use-this
        this._handleDialogClick = (event) => {
            const path = event.composedPath();
            // Check that is not footer or react synthetic events break on buttons
            const isInFooter = path.some((el) => el instanceof HTMLElement && el.slot === 'footer');
            if (!isInFooter) {
                event.stopPropagation(); // solo per backdrop
            }
        };
        this._handleBackdropClick = () => {
            if (!this.staticBackdrop) {
                this.hide();
            }
        };
        this._onTriggerSlotChange = () => {
            this._setupTriggerListeners();
        };
        this._onHeaderSlotChange = () => {
            this.requestUpdate();
        };
        this._onTriggerClick = async (event) => {
            event.stopPropagation();
            const trigger = this._triggerElement;
            if (!trigger)
                return;
            if (isSafari) {
                this._safariMouseInteraction = true;
            }
            await this.updateComplete; // Assicurati che l'update sia completo prima di applicare il focus workaround
            this.show();
        };
        this._onTriggerKeydown = async (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                this.show();
            }
            else {
                const trigger = this._triggerElement;
                if (!trigger)
                    return;
                if (isSafari) {
                    this._safariMouseInteraction = false;
                }
                await this.updateComplete; // Assicurati che l'update sia completo prima di applicare il focus workaround
            }
        };
        this._triggerId = this.generateId('modal-trigger');
        this._titleId = this.generateId('modal-title');
        this._descriptionId = this.generateId('modal-description');
        this._focusTrap = new FocusTrapController(this, {
            getContainer: () => this._modalElement,
            initialFocus: () => this._modalElement,
            getTrigger: () => this._triggerElement,
            onEscape: () => {
                this._safariMouseInteraction = false;
                this.hide();
            },
        });
    }
    connectedCallback() {
        super.connectedCallback?.();
    }
    disconnectedCallback() {
        super.disconnectedCallback?.();
        this._removeTriggerListeners();
        this._cleanupAnimations();
        this._removeInert();
        if (this._restoreFocusTimer !== null) {
            clearTimeout(this._restoreFocusTimer);
            this._restoreFocusTimer = null;
        }
        WindowManager.unlockBodyScroll();
    }
    show() {
        if (this.open || this.isAnimating)
            return;
        // `it-modal-open` is cancelable: calling `preventDefault()` stops the modal
        // from opening (the consumer can open it later by calling `show()` again).
        dispatchCancelable(this, 'it-modal-open', { modal: this }, () => {
            this._originalTrigger = this._triggerElement;
            this.open = true;
        });
    }
    hide() {
        if (!this.open || this.isAnimating)
            return;
        // `it-modal-close` is cancelable: calling `preventDefault()` keeps the modal
        // open (the consumer can close it later by calling `hide()`).
        dispatchCancelable(this, 'it-modal-close', { modal: this }, () => {
            this._hideModal();
        });
    }
    toggle() {
        if (this.open) {
            this.hide();
        }
        else {
            this.show();
        }
    }
    updated(changedProperties) {
        if (changedProperties.has('open')) {
            if (this._isSelfClosing) {
                this._isSelfClosing = false;
                return;
            }
            this._cleanupAnimations();
            if (this.open !== undefined) {
                if (this.open)
                    this._showModal();
                else
                    this._hideModal();
            }
        }
        if (changedProperties.has('_safariMouseInteraction')) {
            let realButton = this._triggerElement;
            if (realButton?.tagName.toLowerCase() === 'it-button') {
                realButton = this._triggerElement?.shadowRoot?.querySelector('button');
            }
            if (!realButton)
                return;
            // Fix Safari: se l'interazione è da mouse, rimuovi outline e box-shadow forzati per evitare il doppio focus ring
            if (this._safariMouseInteraction) {
                realButton.style.setProperty('outline', '0', 'important');
                realButton.style.setProperty('box-shadow', 'none', 'important');
            }
            else {
                realButton.style.removeProperty('outline');
                realButton.style.removeProperty('box-shadow');
            }
        }
    }
    _applyInert() {
        this._removeInert();
        const trigger = this._triggerElement;
        if (trigger) {
            this._triggerPointerBefore = trigger.style.pointerEvents;
            trigger.style.pointerEvents = 'none';
            trigger.setAttribute('inert', '');
            if (trigger.tagName.toLowerCase().startsWith('it-')) {
                trigger.setAttribute('it-inert', '');
            }
        }
        const ignored = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'LINK']);
        for (let cur = this; cur && cur !== document.body; cur = cur.parentElement) {
            const parent = cur.parentElement;
            if (!parent)
                break;
            Array.from(parent.children).forEach((node) => {
                if (node !== cur && !ignored.has(node.tagName) && node instanceof HTMLElement) {
                    node.setAttribute('inert', '');
                    // // FIX FOR SAFARI/VOICEOVER:
                    // // If it's a web component with a shadow root, VoiceOver might still
                    // // find the inner button.
                    if (node.shadowRoot && isSafari) {
                        node.setAttribute('inert', '');
                        node.setAttribute('it-inert', '');
                    }
                    this._inertElements.push(node);
                }
            });
        }
    }
    _removeInert() {
        this._inertElements.forEach((el) => {
            el.removeAttribute('inert');
            el.removeAttribute('it-inert');
        });
        if (this._triggerElement && this._triggerPointerBefore !== null) {
            this._triggerElement.style.pointerEvents = this._triggerPointerBefore;
            this._triggerElement.removeAttribute('inert');
            this._triggerElement.removeAttribute('it-inert');
        }
        this._inertElements = [];
        this._triggerPointerBefore = null;
    }
    async _showModal() {
        if (this.isAnimating)
            return;
        // Cancel any stale close-focus-restore timer so it doesn't fire during the new open.
        if (this._restoreFocusTimer !== null) {
            clearTimeout(this._restoreFocusTimer);
            this._restoreFocusTimer = null;
        }
        this.isAnimating = true;
        this._applyInert();
        WindowManager.lockBodyScroll();
        // ASPETTA LIT o race conditions solo per Safari
        await this.updateComplete;
        requestAnimationFrame(() => {
            // Determine transform based on position
            let dialogStartTransform = 'translate(0, 0)'; // default: no transform
            if (this.position === 'center') {
                dialogStartTransform = 'translate(0, -5%)';
            }
            else if (this.position === 'left') {
                dialogStartTransform = 'translateX(-100%)';
            }
            else if (this.position === 'right') {
                dialogStartTransform = 'translateX(100%)';
            }
            const dialogDuration = !this.disableAnimation && !this.prefersReducedMotion ? this._dialogAnimationDuration : 0;
            const backdropDuration = !this.disableAnimation && !this.prefersReducedMotion ? this._backdropAnimationDuration : 0;
            // Animate backdrop fade in
            if (this._backdropElement) {
                this._backdropAnimation = this._backdropElement.animate([{ opacity: '0' }, { opacity: '0.8' }], {
                    duration: backdropDuration,
                    easing: 'linear',
                    fill: 'forwards',
                });
            }
            // Animate dialog transform (slide/drop in)
            if (this._dialogElement && dialogDuration > 0) {
                this._dialogAnimation = this._dialogElement.animate([{ transform: dialogStartTransform }, { transform: 'translate(0, 0)' }], {
                    duration: dialogDuration,
                    easing: 'ease-in-out',
                    fill: 'forwards',
                });
                this._dialogAnimation.finished
                    .then(() => {
                    if (this.open) {
                        this._focusTrap.activate();
                    }
                })
                    .finally(() => {
                    this.isAnimating = false;
                });
            }
            else {
                // No animation: activate immediately. The 50ms inside activate()'s setTimeout
                // gives VoiceOver iOS enough time to settle its accessibility tree.
                // See FocusTrapController class comment for full rationale.
                this._focusTrap.activate();
                this.isAnimating = false;
            }
        });
    }
    _hideModal() {
        if (this.isAnimating)
            return;
        this.isAnimating = true;
        // Determine end transform based on position
        let dialogEndTransform = 'translate(0, 0)'; // default: no transform
        let dialogCloseDuration = this._dialogAnimationDuration / 1.33;
        if (this.position === 'center') {
            dialogEndTransform = 'translate(0, -5%)';
            // Dialog più veloce quando è centrato per una chiusura più reattiva
            dialogCloseDuration = this._dialogAnimationDuration / 2;
        }
        else if (this.position === 'left') {
            dialogEndTransform = 'translateX(-100%)';
        }
        else if (this.position === 'right') {
            dialogEndTransform = 'translateX(100%)';
        }
        const dialogDuration = !this.disableAnimation && !this.prefersReducedMotion ? dialogCloseDuration : 0;
        const backdropDuration = !this.disableAnimation && !this.prefersReducedMotion ? this._backdropAnimationDuration / 1.33 : 0;
        const finishHide = () => {
            const target = this._originalTrigger ?? this._triggerElement;
            this._removeInert();
            this._cleanupAnimations();
            // skipFocusRestore: we manage focus restore ourselves with the timer below.
            // Without this, _restoreFocus() fires at 0ms and the timer fires at 20ms,
            // causing VoiceOver to announce the restored element twice.
            this._focusTrap.deactivate({ skipFocusRestore: true });
            WindowManager.unlockBodyScroll();
            this._isSelfClosing = true;
            this.isAnimating = false;
            this.open = false;
            if (target) {
                this._restoreFocusTimer = setTimeout(() => {
                    this._restoreFocusTimer = null;
                    if (!this.open)
                        target.focus({ preventScroll: true });
                }, 20);
            }
            this._originalTrigger = null;
            if (isSafari)
                this._safariMouseInteraction = false;
        };
        // Animate dialog and backdrop in parallel for smoother close
        const animations = [];
        if (this._dialogElement && dialogDuration > 0) {
            this._dialogAnimation = this._dialogElement.animate([
                { transform: 'translate(0, 0)', opacity: '1' },
                { transform: dialogEndTransform, opacity: '0' },
            ], {
                duration: dialogDuration,
                easing: 'ease-in',
                fill: 'forwards',
            });
            animations.push(this._dialogAnimation.finished);
        }
        if (this._backdropElement && backdropDuration > 0) {
            this._backdropAnimation = this._backdropElement.animate([{ opacity: '0.8' }, { opacity: '0' }], {
                duration: backdropDuration,
                easing: 'linear',
                fill: 'forwards',
            });
            animations.push(this._backdropAnimation.finished);
        }
        if (animations.length > 0) {
            Promise.all(animations)
                .then(() => finishHide())
                .catch(() => {
                // Animation was cancelled
                finishHide();
            })
                .finally(() => {
                this.isAnimating = false;
            });
        }
        else {
            // No animation, finish immediately
            finishHide();
        }
    }
    _cleanupAnimations() {
        this._dialogAnimation?.cancel();
        this._backdropAnimation?.cancel();
        this._dialogAnimation = undefined;
        this._backdropAnimation = undefined;
    }
    _setupTriggerListeners() {
        const trigger = this._triggerElement;
        if (!trigger)
            return;
        trigger.setAttribute('id', this._triggerId);
        // Rimuovi eventuali listener precedenti
        trigger.removeEventListener('click', this._onTriggerClick);
        trigger.removeEventListener('keydown', this._onTriggerKeydown);
        // Aggiungi nuovi listener
        trigger.addEventListener('click', this._onTriggerClick);
        trigger.addEventListener('keydown', this._onTriggerKeydown);
    }
    _removeTriggerListeners() {
        const trigger = this._triggerElement;
        if (!trigger)
            return;
        trigger.removeEventListener('click', this._onTriggerClick);
        trigger.removeEventListener('keydown', this._onTriggerKeydown);
    }
    get _modalClasses() {
        return {
            modal: true,
            fade: !this.disableAnimation,
            show: Boolean(this.open) && !this.disableAnimation,
            'alert-modal': this.variant === 'alert',
            'popconfirm-modal': this.variant === 'popconfirm',
            'it-dialog-link-list': this.variant === 'link-list',
            'it-dialog-scrollable': this.scrollable || this.position === 'left' || this.position === 'right',
        };
    }
    get _modalBodyClasses() {
        return {
            'modal-dialog': true,
            'modal-dialog-centered': this.position === 'center',
            'modal-dialog-left': this.position === 'left',
            'modal-dialog-right': this.position === 'right',
            'modal-dialog-scrollable': this.scrollable,
            'modal-sm': this.size === 'sm',
            'modal-lg': this.size === 'lg',
            'modal-xl': this.size === 'xl',
        };
    }
    _renderCloseButton() {
        if (this.hideCloseButton || this.variant === 'popconfirm') {
            return '';
        }
        return html `<it-button
      class="btn-close"
      variant="link"
      size="lg"
      part="close-button"
      exportparts="focusable, button,  button:close-button-button"
      @click="${() => this.hide()}"
    >
      <it-icon name="it-close" size="lg"></it-icon>
      <span class="visually-hidden">${this.closeLabel}</span>
    </it-button>`;
    }
    render() {
        const hasHeader = (this.modalTitle || this._headerSlot?.assignedElements({ flatten: true }).length > 0) && !this.customHeader;
        const enableFocusContent = this.scrollable || this.position === 'left' || this.position === 'right';
        let ariaLabelledBy = hasHeader ? this._titleId : undefined;
        if (this.itAriaLabel) {
            ariaLabelledBy = undefined;
        }
        return html `
      <slot name="trigger" @slotchange=${this._onTriggerSlotChange}></slot>
      <div
        class="${classMap(this._modalClasses)}"
        role="dialog"
        aria-modal="true"
        aria-labelledby="${ifDefined(ariaLabelledBy)}"
        aria-describedby="${ifDefined(this._descriptionId)}"
        aria-label="${ifDefined(this.itAriaLabel || undefined)}"
        aria-hidden="${!this.open}"
        tabindex="-1"
        @click="${this._handleBackdropClick}"
        part="modal"
      >
        <!-- TRICK SAFARI ENGINE -->
        <div
          id="safari-focus-anchor"
          tabindex="-1"
          aria-hidden="true"
          style="position: absolute; width: 0; height: 0; outline: none;"
        ></div>
        <div
          class="${classMap(this._modalBodyClasses)}"
          role="document"
          @click="${this._handleDialogClick}"
          part="modal-content-wrapper"
        >
          ${this.closeButtonPlacement === 'backdrop' ? this._renderCloseButton() : ''}
          <div class="visually-hidden" id="${this._descriptionId}">
            <slot name="description" @slotchange="${this._onHeaderSlotChange}">${this.modalDescription}</slot>
          </div>
          <div class="modal-content" part="modal-content">
            <div
              class="${hasHeader || (this.variant !== 'popconfirm' && !this.hideCloseButton) ? 'modal-header' : ''}"
              part="modal-header"
            >
              <slot name="header-icon"></slot>
              ${hasHeader
            ? html `<h2 id="${this._titleId}" class="modal-title">
                    <slot name="header" @slotchange="${this._onHeaderSlotChange}">${this.modalTitle}</slot>
                  </h2>`
            : html `<div><slot name="header" @slotchange="${this._onHeaderSlotChange}"></slot></div>`}
              ${this.closeButtonPlacement === 'header' ? this._renderCloseButton() : ''}
            </div>
            <div class="modal-body" tabindex="${enableFocusContent ? '0' : '-1'}" part="focusable modal-body">
              <slot name="content"></slot>
            </div>
            <div class="modal-footer ${this.footerShadow ? 'modal-footer-shadow' : ''}" part="modal-footer">
              <slot name="footer"></slot>
            </div>
          </div>
        </div>
      </div>
      <div
        class="modal-backdrop ${this.disableAnimation ? 'fade' : ''} ${this.open ? 'show' : ''}"
        aria-hidden="true"
        @click="${() => !this.staticBackdrop && this.hide()}"
        part="modal-backdrop"
      ></div>
    `;
    }
};
ItModal.styles = styles;
ItModal.shadowRootOptions = { ...BaseComponent.shadowRootOptions, delegatesFocus: true };
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItModal.prototype, "open", void 0);
__decorate([
    property({ type: String, attribute: 'modal-title', reflect: true }),
    __metadata("design:type", Object)
], ItModal.prototype, "modalTitle", void 0);
__decorate([
    property({ type: String, attribute: 'modal-description', reflect: true }),
    __metadata("design:type", Object)
], ItModal.prototype, "modalDescription", void 0);
__decorate([
    property({ type: Boolean, attribute: 'custom-header', reflect: true }),
    __metadata("design:type", Object)
], ItModal.prototype, "customHeader", void 0);
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", String)
], ItModal.prototype, "size", void 0);
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", String)
], ItModal.prototype, "position", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItModal.prototype, "scrollable", void 0);
__decorate([
    property({ type: Boolean, attribute: 'static-backdrop', reflect: true }),
    __metadata("design:type", Object)
], ItModal.prototype, "staticBackdrop", void 0);
__decorate([
    property({ type: Boolean, attribute: 'hide-close-button', reflect: true }),
    __metadata("design:type", Object)
], ItModal.prototype, "hideCloseButton", void 0);
__decorate([
    property({ type: String, attribute: 'close-button-placement', reflect: true }),
    __metadata("design:type", String)
], ItModal.prototype, "closeButtonPlacement", void 0);
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", String)
], ItModal.prototype, "variant", void 0);
__decorate([
    property({ type: String, attribute: 'it-aria-label' }),
    __metadata("design:type", Object)
], ItModal.prototype, "itAriaLabel", void 0);
__decorate([
    property({ type: String, attribute: 'close-label', reflect: true }),
    __metadata("design:type", Object)
], ItModal.prototype, "closeLabel", void 0);
__decorate([
    property({ type: Boolean, attribute: 'disable-animation', reflect: true }),
    __metadata("design:type", Object)
], ItModal.prototype, "disableAnimation", void 0);
__decorate([
    property({ type: Boolean, attribute: 'footer-shadow', reflect: true }),
    __metadata("design:type", Object)
], ItModal.prototype, "footerShadow", void 0);
__decorate([
    query('[role="dialog"]'),
    __metadata("design:type", HTMLElement)
], ItModal.prototype, "_modalElement", void 0);
__decorate([
    query('slot[name="trigger"]'),
    __metadata("design:type", HTMLSlotElement)
], ItModal.prototype, "_triggerSlot", void 0);
__decorate([
    query('slot[name="header"]'),
    __metadata("design:type", HTMLSlotElement)
], ItModal.prototype, "_headerSlot", void 0);
__decorate([
    query('slot[name="description"]'),
    __metadata("design:type", HTMLSlotElement)
], ItModal.prototype, "_descriptionSlot", void 0);
__decorate([
    query('.modal-backdrop'),
    __metadata("design:type", HTMLElement)
], ItModal.prototype, "_backdropElement", void 0);
__decorate([
    query('.modal-dialog'),
    __metadata("design:type", HTMLElement)
], ItModal.prototype, "_dialogElement", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], ItModal.prototype, "_safariMouseInteraction", void 0);
ItModal = __decorate([
    customElement('it-modal'),
    __metadata("design:paramtypes", [])
], ItModal);

export { ItModal };
//# sourceMappingURL=it-modal.js.map
